import { z, ZodError } from 'zod';
import { parseISO, isValid, format } from 'date-fns';
import { fr } from 'date-fns/locale';

// ../../core/shared/dist/index.mjs
var Currency = /* @__PURE__ */ ((Currency2) => {
  Currency2["EUR"] = "EUR";
  Currency2["CHF"] = "CHF";
  Currency2["MAD"] = "MAD";
  return Currency2;
})(Currency || {});
var CURRENCY_INFO = {
  [
    "EUR"
    /* EUR */
  ]: {
    code: "EUR",
    name: "Euro",
    symbol: "\u20AC",
    locale: "fr-FR"
  },
  [
    "CHF"
    /* CHF */
  ]: {
    code: "CHF",
    name: "Franc suisse",
    symbol: "CHF",
    locale: "fr-CH"
  },
  [
    "MAD"
    /* MAD */
  ]: {
    code: "MAD",
    name: "Dirham marocain",
    symbol: "MAD",
    locale: "fr-MA"
  }
};
var SUPPORTED_CURRENCIES = Object.values(Currency);
var DEFAULT_CURRENCY = "EUR";
var passwordRules = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true
};
var passwordSchema = z.string().min(passwordRules.minLength, `Le mot de passe doit contenir au moins ${passwordRules.minLength} caracteres`).regex(/[A-Z]/, "Le mot de passe doit contenir au moins une majuscule").regex(/[a-z]/, "Le mot de passe doit contenir au moins une minuscule").regex(/[0-9]/, "Le mot de passe doit contenir au moins un chiffre");
var loginSchema = z.object({
  email: z.string().email("Email invalide"),
  password: z.string().min(1, "Mot de passe requis")
});
var forgotPasswordSchema = z.object({
  email: z.string().email("Email invalide")
});
var setPasswordSchema = z.object({
  token: z.string().min(1, "Token requis"),
  password: passwordSchema
});
var validateTokenSchema = z.object({
  token: z.string().min(1, "Token requis")
});
var UserRole = /* @__PURE__ */ ((UserRole22) => {
  UserRole22["OWNER"] = "OWNER";
  UserRole22["ADMIN"] = "ADMIN";
  UserRole22["MANAGER"] = "MANAGER";
  UserRole22["OPERATOR"] = "OPERATOR";
  UserRole22["USER"] = "OPERATOR";
  UserRole22["VIEWER"] = "VIEWER";
  return UserRole22;
})(UserRole || {});
var RoleHierarchy = {
  [
    "OWNER"
    /* OWNER */
  ]: 80,
  [
    "ADMIN"
    /* ADMIN */
  ]: 60,
  [
    "MANAGER"
    /* MANAGER */
  ]: 50,
  [
    "OPERATOR"
    /* OPERATOR */
  ]: 40,
  [
    "VIEWER"
    /* VIEWER */
  ]: 20
};
function hasMinimumRole(userRole, requiredRole) {
  return RoleHierarchy[userRole] >= RoleHierarchy[requiredRole];
}
var ActivationStatus = /* @__PURE__ */ ((ActivationStatus2) => {
  ActivationStatus2["ACTIVE"] = "ACTIVE";
  ActivationStatus2["INACTIVE"] = "INACTIVE";
  ActivationStatus2["PENDING"] = "PENDING";
  return ActivationStatus2;
})(ActivationStatus || {});
var TokenType = /* @__PURE__ */ ((TokenType2) => {
  TokenType2["ACTIVATION"] = "ACTIVATION";
  TokenType2["PASSWORD_RESET"] = "PASSWORD_RESET";
  return TokenType2;
})(TokenType || {});
var Product = /* @__PURE__ */ ((Product3) => {
  Product3["BSTOCK"] = "BSTOCK";
  Product3["BSTAFF"] = "BSTAFF";
  Product3["BDISPLAY"] = "BDISPLAY";
  return Product3;
})(Product || {});
var createUserSchema = z.object({
  email: z.string().email("Email invalide"),
  firstName: z.string().min(2, "Le prenom doit contenir au moins 2 caracteres"),
  lastName: z.string().min(2, "Le nom doit contenir au moins 2 caracteres"),
  role: z.nativeEnum(UserRole),
  /**
   * Module d'origine de la creation (US-MAC.8). Si fourni, seul le role de
   * ce module est pose ; les 2 autres colonnes restent null.
   */
  module: z.nativeEnum(Product).optional()
});
var updateUserSchema = z.object({
  firstName: z.string().min(2, "Le prenom doit contenir au moins 2 caracteres").optional(),
  lastName: z.string().min(2, "Le nom doit contenir au moins 2 caracteres").optional(),
  isActive: z.boolean().optional()
});
var updateUserRoleSchema = z.object({
  role: z.nativeEnum(UserRole)
});
var getUsersQuerySchema = z.object({
  search: z.string().optional(),
  role: z.nativeEnum(UserRole).optional(),
  status: z.enum(["active", "inactive", "pending"]).optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20)
});
var createUserWithPasswordSchema = z.object({
  email: z.string().email("Email invalide"),
  password: z.string().min(8, "Le mot de passe doit contenir au moins 8 caracteres"),
  firstName: z.string().min(2, "Le prenom doit contenir au moins 2 caracteres"),
  lastName: z.string().min(2, "Le nom doit contenir au moins 2 caracteres"),
  role: z.nativeEnum(UserRole),
  brandIds: z.array(z.string().uuid()).optional()
});
var BrandType = {
  GROUP: "GROUP",
  POINT_OF_SALE: "POINT_OF_SALE",
  CENTRAL: "CENTRAL"
};
var createBrandSchema = z.object({
  name: z.string().min(2, "Le nom du brand doit contenir au moins 2 caracteres"),
  type: z.enum(["GROUP", "POINT_OF_SALE", "CENTRAL"]),
  parentId: z.string().cuid().optional()
});
var updateBrandSchema = z.object({
  name: z.string().min(2).optional(),
  type: z.enum(["GROUP", "POINT_OF_SALE", "CENTRAL"]).optional(),
  isActive: z.boolean().optional(),
  parentId: z.string().cuid().optional()
});
var updateBrandSettingsSchema = z.object({
  name: z.string().min(2, "Le nom doit contenir au moins 2 caracteres").optional(),
  address: z.string().min(1).nullable().optional(),
  city: z.string().min(1).nullable().optional(),
  postalCode: z.string().min(1).nullable().optional(),
  phone: z.string().min(1).nullable().optional(),
  email: z.string().email("Adresse email invalide").nullable().optional(),
  legalId: z.string().min(1).nullable().optional(),
  cutoffHour: z.number().int().min(0).max(23).optional()
});
z.object({
  fullTimeHours: z.number().positive().max(168).optional(),
  overtime25MaxHours: z.number().min(0).max(168).optional(),
  nightStartHour: z.number().int().min(0).max(24).optional(),
  nightEndHour: z.number().int().min(0).max(24).optional()
}).refine((data) => data.nightStartHour === void 0 || data.nightEndHour === void 0 || data.nightStartHour !== data.nightEndHour, {
  message: "L'heure de debut et de fin ne peuvent pas etre identiques",
  path: ["nightEndHour"]
});
var createCompanySchema = z.object({
  name: z.string().min(2, "Le nom de l'entreprise doit contenir au moins 2 caracteres")
});
var updateCompanySchema = z.object({
  name: z.string().min(2, "Le nom de l'entreprise doit contenir au moins 2 caracteres").optional()
});
var createAdminCompanySchema = z.object({
  name: z.string().min(2, "Nom: minimum 2 caracteres").max(200, "Nom: maximum 200 caracteres"),
  country: z.string().max(100, "Pays: maximum 100 caracteres").optional().default("FR"),
  currency: z.nativeEnum(Currency).optional().default(
    "EUR"
    /* EUR */
  )
});
var updateAdminCompanySchema = z.object({
  name: z.string().min(2).max(200).optional(),
  country: z.string().max(100).optional(),
  currency: z.nativeEnum(Currency).optional(),
  isActive: z.boolean().optional(),
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Couleur invalide (format: #RRGGBB)").nullish().transform((val) => val || null)
});
var timezoneSchema = z.string().max(50).regex(/^[A-Za-z_\/]+$/, "Timezone invalide (format: Area/Location)");
var createAdminBrandSchema = z.object({
  name: z.string().min(2, "Nom: minimum 2 caracteres").max(200, "Nom: maximum 200 caracteres"),
  type: z.enum(["GROUP", "POINT_OF_SALE", "CENTRAL"]),
  parentId: z.string().uuid("ID parent invalide").optional(),
  timezone: timezoneSchema.optional().default("Europe/Paris")
}).refine(
  (data) => {
    if ((data.type === "POINT_OF_SALE" || data.type === "CENTRAL") && !data.parentId) {
      return false;
    }
    if (data.type === "GROUP" && data.parentId) {
      return false;
    }
    return true;
  },
  {
    message: "Un SITE/CENTRAL doit avoir un parent GROUP, un GROUP ne peut pas avoir de parent",
    path: ["parentId"]
  }
);
var updateAdminBrandSchema = z.object({
  name: z.string().min(2).max(200).optional(),
  isActive: z.boolean().optional(),
  timezone: timezoneSchema.optional()
});
var updateCompanyBrandingSchema = z.object({
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Couleur primaire: format hexadecimal requis (ex: #FF5733)").nullable().optional()
});
var dateOrDateTimeString = z.string().regex(
  /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2})?$/,
  "Date invalide (format: YYYY-MM-DD ou YYYY-MM-DDTHH:mm)"
);
var isoDateTimeString = z.string().datetime("Date invalide (format ISO 8601 attendu)");
var auditActionEnum = z.enum([
  "CREATE",
  "UPDATE",
  "DELETE",
  "VALIDATE",
  "CANCEL",
  "LOGIN",
  "LOGOUT"
]);
var appSourceEnum = z.enum(["BSTOCK", "BSTAFF", "BDISPLAY", "CORE"]);
var queryAuditLogsSchema = z.object({
  app: appSourceEnum.optional(),
  actorId: z.string().uuid().optional(),
  entityType: z.string().optional(),
  entityId: z.string().uuid().optional(),
  eventType: z.string().optional(),
  action: auditActionEnum.optional(),
  dateFrom: dateOrDateTimeString.optional(),
  dateTo: dateOrDateTimeString.optional(),
  search: z.string().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(50)
});
var queryEntityHistorySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(50)
});
var queryAuditStatsSchema = z.object({
  dateFrom: dateOrDateTimeString.optional(),
  dateTo: dateOrDateTimeString.optional()
});
var PERMISSION_MATRIX = {
  "stock.read": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR",
    "VIEWER"
    /* VIEWER */
  ],
  "stock.read_financials": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "inventories.read": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR",
    "VIEWER"
    /* VIEWER */
  ],
  "inventories.create": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR"
    /* OPERATOR */
  ],
  "inventories.edit": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR"
    /* OPERATOR */
  ],
  "inventories.validate": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "inventories.delete": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "goods-receipts.read": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR",
    "VIEWER"
    /* VIEWER */
  ],
  "goods-receipts.create": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR"
    /* OPERATOR */
  ],
  "goods-receipts.validate": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "goods-receipts.delete": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "purchase-orders.read": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR",
    "VIEWER"
    /* VIEWER */
  ],
  "purchase-orders.create": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR"
    /* OPERATOR */
  ],
  "purchase-orders.send": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "purchase-orders.delete": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "losses.read": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR",
    "VIEWER"
    /* VIEWER */
  ],
  "losses.create": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR"
    /* OPERATOR */
  ],
  "losses.delete": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "transfers.read": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR",
    "VIEWER"
    /* VIEWER */
  ],
  "transfers.create": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR"
    /* OPERATOR */
  ],
  "transfers.ship": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "transfers.receive": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "transfers.delete": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "supplier-returns.read": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR",
    "VIEWER"
    /* VIEWER */
  ],
  "supplier-returns.create": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "supplier-returns.validate": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "supplier-returns.delete": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "raw-products.read": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR",
    "VIEWER"
    /* VIEWER */
  ],
  "raw-products.create": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "raw-products.delete": [
    "OWNER",
    "ADMIN"
    /* ADMIN */
  ],
  "pos-products.read": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "pos-products.create": [
    "OWNER",
    "ADMIN"
    /* ADMIN */
  ],
  "pos-products.delete": [
    "OWNER",
    "ADMIN"
    /* ADMIN */
  ],
  "recipes.read": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR",
    "VIEWER"
    /* VIEWER */
  ],
  "recipes.create": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "recipes.delete": [
    "OWNER",
    "ADMIN"
    /* ADMIN */
  ],
  "reports.read": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "VIEWER"
    /* VIEWER */
  ],
  "reports.read_financials": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "users.read": [
    "OWNER",
    "ADMIN"
    /* ADMIN */
  ],
  "users.create": [
    "OWNER",
    "ADMIN"
    /* ADMIN */
  ],
  "users.edit": [
    "OWNER",
    "ADMIN"
    /* ADMIN */
  ],
  "users.delete": [
    "OWNER",
    "ADMIN"
    /* ADMIN */
  ],
  "settings.read": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "settings.edit": [
    "OWNER",
    "ADMIN"
    /* ADMIN */
  ],
  "suppliers.read": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR"
    /* OPERATOR */
  ],
  "suppliers.create": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "suppliers.delete": [
    "OWNER",
    "ADMIN"
    /* ADMIN */
  ],
  "audit.read": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "pos-sales.read": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR",
    "VIEWER"
    /* VIEWER */
  ],
  "pos-sales.sync": [
    "OWNER",
    "ADMIN"
    /* ADMIN */
  ],
  // === BStaff ===
  "employees.read": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR",
    "VIEWER"
    /* VIEWER */
  ],
  "employees.write": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "employees.delete": [
    "OWNER"
    /* OWNER */
  ],
  "clock-entries.read": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR",
    "VIEWER"
    /* VIEWER */
  ],
  "clock-entries.write": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "clock-entries.delete": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "contracts.read": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "contracts.write": [
    "OWNER",
    "ADMIN"
    /* ADMIN */
  ],
  "attendance.read": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR",
    "VIEWER"
    /* VIEWER */
  ],
  "analytics.read": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "kiosk.configure": [
    "OWNER",
    "ADMIN"
    /* ADMIN */
  ],
  "absences.read": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR",
    "VIEWER"
    /* VIEWER */
  ],
  "absences.write": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  // === BDisplay (legacy grossier — deprecated) ===
  "bdisplay.read": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR",
    "VIEWER"
    /* VIEWER */
  ],
  "bdisplay.write": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "bdisplay.admin": [
    "OWNER",
    "ADMIN"
    /* ADMIN */
  ],
  // === BDisplay (granularite fine US-MAC.5) ===
  "media.read": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR",
    "VIEWER"
    /* VIEWER */
  ],
  "media.write": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR"
    /* OPERATOR */
  ],
  "media.delete": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "screens.read": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR",
    "VIEWER"
    /* VIEWER */
  ],
  "screens.write": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "screens.delete": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "screens.remote.read": [
    "OWNER"
    /* OWNER */
  ],
  "screens.remote.write": [
    "OWNER"
    /* OWNER */
  ],
  "playlists.read": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR",
    "VIEWER"
    /* VIEWER */
  ],
  "playlists.write": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR"
    /* OPERATOR */
  ],
  "playlists.delete": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "programmations.read": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR",
    "VIEWER"
    /* VIEWER */
  ],
  "programmations.write": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR"
    /* OPERATOR */
  ],
  "programmations.delete": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "tags.read": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR",
    "VIEWER"
    /* VIEWER */
  ],
  "tags.write": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ],
  "menu-boards.read": [
    "OWNER",
    "ADMIN",
    "MANAGER",
    "OPERATOR",
    "VIEWER"
    /* VIEWER */
  ],
  "menu-boards.write": [
    "OWNER",
    "ADMIN",
    "MANAGER"
    /* MANAGER */
  ]
};
function hasPermission(role, permission) {
  const allowedRoles = PERMISSION_MATRIX[permission];
  if (!allowedRoles) return false;
  return allowedRoles.includes(role);
}
var PERMISSION_TO_PRODUCT = {
  // ===========================================================================
  // BStock
  // ===========================================================================
  "stock.read": "BSTOCK",
  "stock.read_financials": "BSTOCK",
  "inventories.read": "BSTOCK",
  "inventories.create": "BSTOCK",
  "inventories.edit": "BSTOCK",
  "inventories.validate": "BSTOCK",
  "inventories.delete": "BSTOCK",
  "goods-receipts.read": "BSTOCK",
  "goods-receipts.create": "BSTOCK",
  "goods-receipts.validate": "BSTOCK",
  "goods-receipts.delete": "BSTOCK",
  "purchase-orders.read": "BSTOCK",
  "purchase-orders.create": "BSTOCK",
  "purchase-orders.send": "BSTOCK",
  "purchase-orders.delete": "BSTOCK",
  "losses.read": "BSTOCK",
  "losses.create": "BSTOCK",
  "losses.delete": "BSTOCK",
  "transfers.read": "BSTOCK",
  "transfers.create": "BSTOCK",
  "transfers.ship": "BSTOCK",
  "transfers.receive": "BSTOCK",
  "transfers.delete": "BSTOCK",
  "supplier-returns.read": "BSTOCK",
  "supplier-returns.create": "BSTOCK",
  "supplier-returns.validate": "BSTOCK",
  "supplier-returns.delete": "BSTOCK",
  "raw-products.read": "BSTOCK",
  "raw-products.create": "BSTOCK",
  "raw-products.delete": "BSTOCK",
  "pos-products.read": "BSTOCK",
  "pos-products.create": "BSTOCK",
  "pos-products.delete": "BSTOCK",
  "recipes.read": "BSTOCK",
  "recipes.create": "BSTOCK",
  "recipes.delete": "BSTOCK",
  "reports.read": "BSTOCK",
  "reports.read_financials": "BSTOCK",
  "suppliers.read": "BSTOCK",
  "suppliers.create": "BSTOCK",
  "suppliers.delete": "BSTOCK",
  "pos-sales.read": "BSTOCK",
  "pos-sales.sync": "BSTOCK",
  // ===========================================================================
  // BStaff
  // ===========================================================================
  "employees.read": "BSTAFF",
  "employees.write": "BSTAFF",
  "employees.delete": "BSTAFF",
  "clock-entries.read": "BSTAFF",
  "clock-entries.write": "BSTAFF",
  "clock-entries.delete": "BSTAFF",
  "contracts.read": "BSTAFF",
  "contracts.write": "BSTAFF",
  "attendance.read": "BSTAFF",
  "analytics.read": "BSTAFF",
  "kiosk.configure": "BSTAFF",
  "absences.read": "BSTAFF",
  "absences.write": "BSTAFF",
  // ===========================================================================
  // BDisplay
  // ===========================================================================
  // Legacy grossier (deprecated, retro-compat)
  "bdisplay.read": "BDISPLAY",
  "bdisplay.write": "BDISPLAY",
  "bdisplay.admin": "BDISPLAY",
  // Granularite fine (US-MAC.5)
  "media.read": "BDISPLAY",
  "media.write": "BDISPLAY",
  "media.delete": "BDISPLAY",
  "screens.read": "BDISPLAY",
  "screens.write": "BDISPLAY",
  "screens.delete": "BDISPLAY",
  "screens.remote.read": "BDISPLAY",
  "screens.remote.write": "BDISPLAY",
  "playlists.read": "BDISPLAY",
  "playlists.write": "BDISPLAY",
  "playlists.delete": "BDISPLAY",
  "programmations.read": "BDISPLAY",
  "programmations.write": "BDISPLAY",
  "programmations.delete": "BDISPLAY",
  "tags.read": "BDISPLAY",
  "tags.write": "BDISPLAY",
  // Anticipation Phase 11
  "menu-boards.read": "BDISPLAY",
  "menu-boards.write": "BDISPLAY"
  /* BDISPLAY */
  // ===========================================================================
  // Transverses (NON listees ici — accessibles avec n'importe quel module) :
  //   'users.read', 'users.create', 'users.edit', 'users.delete',
  //   'settings.read', 'settings.edit',
  //   'audit.read'
  // ===========================================================================
};
(() => {
  const map = {
    [
      "BSTOCK"
      /* BSTOCK */
    ]: [],
    [
      "BSTAFF"
      /* BSTAFF */
    ]: [],
    [
      "BDISPLAY"
      /* BDISPLAY */
    ]: []
  };
  for (const [perm, product] of Object.entries(PERMISSION_TO_PRODUCT)) {
    if (product) map[product].push(perm);
  }
  return map;
})();
var AppSource = {
  BSTOCK: "BSTOCK",
  BSTAFF: "BSTAFF",
  BDISPLAY: "BDISPLAY",
  CORE: "CORE"
};

// src/enums/supplier-access-scope.enum.ts
var SupplierAccessScope = {
  CENTRAL_ONLY: "CENTRAL_ONLY",
  SITES_ONLY: "SITES_ONLY",
  BOTH: "BOTH"
};

// src/enums/market-type.enum.ts
var MarketType = /* @__PURE__ */ ((MarketType2) => {
  MarketType2["FOOD"] = "FOOD";
  MarketType2["FOOD_IND"] = "FOOD_IND";
  MarketType2["NON_FOOD"] = "NON_FOOD";
  MarketType2["NON_FOOD_IND"] = "NON_FOOD_IND";
  MarketType2["MAINTENANCE"] = "MAINTENANCE";
  return MarketType2;
})(MarketType || {});
var MarketTypeLabels = {
  ["FOOD" /* FOOD */]: "Alimentaire",
  ["FOOD_IND" /* FOOD_IND */]: "Alimentaire Ind.",
  ["NON_FOOD" /* NON_FOOD */]: "Non-Food",
  ["NON_FOOD_IND" /* NON_FOOD_IND */]: "Non-Food Ind.",
  ["MAINTENANCE" /* MAINTENANCE */]: "Maintenance"
};
var MarketTypeIcons = {
  ["FOOD" /* FOOD */]: "\u{1F37D}\uFE0F",
  ["FOOD_IND" /* FOOD_IND */]: "\u{1F3ED}",
  ["NON_FOOD" /* NON_FOOD */]: "\u{1F4E6}",
  ["NON_FOOD_IND" /* NON_FOOD_IND */]: "\u{1F3D7}\uFE0F",
  ["MAINTENANCE" /* MAINTENANCE */]: "\u{1F9F9}"
};

// src/enums/status.enum.ts
var Status = /* @__PURE__ */ ((Status2) => {
  Status2["ACTIVE"] = "ACTIVE";
  Status2["INACTIVE"] = "INACTIVE";
  Status2["ARCHIVED"] = "ARCHIVED";
  return Status2;
})(Status || {});
var OrderStatus = /* @__PURE__ */ ((OrderStatus2) => {
  OrderStatus2["DRAFT"] = "DRAFT";
  OrderStatus2["SENT"] = "SENT";
  OrderStatus2["CONFIRMED"] = "CONFIRMED";
  OrderStatus2["PARTIALLY_RECEIVED"] = "PARTIALLY_RECEIVED";
  OrderStatus2["COMPLETED"] = "COMPLETED";
  OrderStatus2["CANCELLED"] = "CANCELLED";
  return OrderStatus2;
})(OrderStatus || {});
var InventoryStatus = /* @__PURE__ */ ((InventoryStatus2) => {
  InventoryStatus2["DRAFT"] = "DRAFT";
  InventoryStatus2["IN_PROGRESS"] = "IN_PROGRESS";
  InventoryStatus2["VALIDATED"] = "VALIDATED";
  InventoryStatus2["CANCELLED"] = "CANCELLED";
  return InventoryStatus2;
})(InventoryStatus || {});

// src/enums/pos.enum.ts
var PosProductType = {
  PRODUCT: "PRODUCT",
  OPTION: "OPTION"
};
var SyncStatus = {
  SYNCED: "SYNCED",
  PENDING: "PENDING",
  ERROR: "ERROR"
};
var PosProductSortField = {
  NAME: "name",
  SKU: "sku",
  TYPE: "type",
  POS_CATEGORY: "posCategory",
  SELL_PRICE: "sellPrice",
  SYNC_STATUS: "syncStatus"
};
var SortOrder = {
  ASC: "asc",
  DESC: "desc"
};

// src/enums/unit-type.enum.ts
var UnitType = /* @__PURE__ */ ((UnitType2) => {
  UnitType2["MASS"] = "MASS";
  UnitType2["VOLUME"] = "VOLUME";
  UnitType2["PIECE"] = "PIECE";
  return UnitType2;
})(UnitType || {});
var UnitTypeLabels = {
  ["MASS" /* MASS */]: "Masse",
  ["VOLUME" /* VOLUME */]: "Volume",
  ["PIECE" /* PIECE */]: "Piece"
};

// src/enums/purchase-order-status.enum.ts
var PurchaseOrderStatus = /* @__PURE__ */ ((PurchaseOrderStatus2) => {
  PurchaseOrderStatus2["DRAFT"] = "DRAFT";
  PurchaseOrderStatus2["SENT"] = "SENT";
  PurchaseOrderStatus2["PARTIALLY_RECEIVED"] = "PARTIALLY_RECEIVED";
  PurchaseOrderStatus2["RECEIVED"] = "RECEIVED";
  PurchaseOrderStatus2["CANCELLED"] = "CANCELLED";
  return PurchaseOrderStatus2;
})(PurchaseOrderStatus || {});
var PURCHASE_ORDER_STATUS_LABELS = {
  ["DRAFT" /* DRAFT */]: "Brouillon",
  ["SENT" /* SENT */]: "Envoyee",
  ["PARTIALLY_RECEIVED" /* PARTIALLY_RECEIVED */]: "Part. recue",
  ["RECEIVED" /* RECEIVED */]: "Recue",
  ["CANCELLED" /* CANCELLED */]: "Annulee"
};
var PURCHASE_ORDER_STATUS_COLORS = {
  ["DRAFT" /* DRAFT */]: "gray",
  ["SENT" /* SENT */]: "blue",
  ["PARTIALLY_RECEIVED" /* PARTIALLY_RECEIVED */]: "orange",
  ["RECEIVED" /* RECEIVED */]: "green",
  ["CANCELLED" /* CANCELLED */]: "red"
};

// src/enums/goods-receipt-status.enum.ts
var GoodsReceiptStatus = /* @__PURE__ */ ((GoodsReceiptStatus2) => {
  GoodsReceiptStatus2["DRAFT"] = "DRAFT";
  GoodsReceiptStatus2["VALIDATED"] = "VALIDATED";
  GoodsReceiptStatus2["CANCELLED"] = "CANCELLED";
  return GoodsReceiptStatus2;
})(GoodsReceiptStatus || {});
var GOODS_RECEIPT_STATUS_LABELS = {
  ["DRAFT" /* DRAFT */]: "Brouillon",
  ["VALIDATED" /* VALIDATED */]: "Validee",
  ["CANCELLED" /* CANCELLED */]: "Annulee"
};
var GOODS_RECEIPT_STATUS_COLORS = {
  ["DRAFT" /* DRAFT */]: "gray",
  ["VALIDATED" /* VALIDATED */]: "green",
  ["CANCELLED" /* CANCELLED */]: "red"
};
var VarianceReason = /* @__PURE__ */ ((VarianceReason2) => {
  VarianceReason2["SUPPLIER_SHORTAGE"] = "SUPPLIER_SHORTAGE";
  VarianceReason2["DELIVERY_ERROR"] = "DELIVERY_ERROR";
  VarianceReason2["DAMAGED"] = "DAMAGED";
  VarianceReason2["OTHER"] = "OTHER";
  return VarianceReason2;
})(VarianceReason || {});
var VARIANCE_REASON_LABELS = {
  ["SUPPLIER_SHORTAGE" /* SUPPLIER_SHORTAGE */]: "Rupture fournisseur",
  ["DELIVERY_ERROR" /* DELIVERY_ERROR */]: "Erreur de livraison",
  ["DAMAGED" /* DAMAGED */]: "Produit endommage",
  ["OTHER" /* OTHER */]: "Autre"
};
var PMPSourceType = /* @__PURE__ */ ((PMPSourceType2) => {
  PMPSourceType2["GOODS_RECEIPT"] = "GOODS_RECEIPT";
  PMPSourceType2["INITIAL"] = "INITIAL";
  PMPSourceType2["MANUAL_ADJUSTMENT"] = "MANUAL_ADJUSTMENT";
  return PMPSourceType2;
})(PMPSourceType || {});
var PMP_SOURCE_TYPE_LABELS = {
  ["GOODS_RECEIPT" /* GOODS_RECEIPT */]: "Reception",
  ["INITIAL" /* INITIAL */]: "Initial",
  ["MANUAL_ADJUSTMENT" /* MANUAL_ADJUSTMENT */]: "Ajustement manuel"
};

// src/enums/stock-movement.enum.ts
var MovementType = /* @__PURE__ */ ((MovementType2) => {
  MovementType2["IN"] = "IN";
  MovementType2["OUT"] = "OUT";
  return MovementType2;
})(MovementType || {});
var MOVEMENT_TYPE_LABELS = {
  ["IN" /* IN */]: "Entree",
  ["OUT" /* OUT */]: "Sortie"
};
var MovementReason = /* @__PURE__ */ ((MovementReason2) => {
  MovementReason2["RECEPTION"] = "RECEPTION";
  MovementReason2["POS_SALE"] = "POS_SALE";
  MovementReason2["LOSS"] = "LOSS";
  MovementReason2["TRANSFER_IN"] = "TRANSFER_IN";
  MovementReason2["TRANSFER_OUT"] = "TRANSFER_OUT";
  MovementReason2["ADJUSTMENT"] = "ADJUSTMENT";
  MovementReason2["INVENTORY"] = "INVENTORY";
  MovementReason2["SUPPLIER_RETURN"] = "SUPPLIER_RETURN";
  return MovementReason2;
})(MovementReason || {});
var MOVEMENT_REASON_LABELS = {
  ["RECEPTION" /* RECEPTION */]: "Reception",
  ["POS_SALE" /* POS_SALE */]: "Vente POS",
  ["LOSS" /* LOSS */]: "Perte",
  ["TRANSFER_IN" /* TRANSFER_IN */]: "Transfert entrant",
  ["TRANSFER_OUT" /* TRANSFER_OUT */]: "Transfert sortant",
  ["ADJUSTMENT" /* ADJUSTMENT */]: "Ajustement",
  ["INVENTORY" /* INVENTORY */]: "Inventaire",
  ["SUPPLIER_RETURN" /* SUPPLIER_RETURN */]: "Retour fournisseur"
};
var MOVEMENT_REASON_COLORS = {
  ["RECEPTION" /* RECEPTION */]: "success",
  ["POS_SALE" /* POS_SALE */]: "neutral",
  ["LOSS" /* LOSS */]: "danger",
  ["TRANSFER_IN" /* TRANSFER_IN */]: "info",
  ["TRANSFER_OUT" /* TRANSFER_OUT */]: "warning",
  ["ADJUSTMENT" /* ADJUSTMENT */]: "info",
  ["INVENTORY" /* INVENTORY */]: "primary",
  ["SUPPLIER_RETURN" /* SUPPLIER_RETURN */]: "warning"
};
var SourceType = /* @__PURE__ */ ((SourceType2) => {
  SourceType2["GOODS_RECEIPT"] = "GOODS_RECEIPT";
  SourceType2["THEORETICAL_CONSUMPTION"] = "THEORETICAL_CONSUMPTION";
  SourceType2["LOSS"] = "LOSS";
  SourceType2["TRANSFER"] = "TRANSFER";
  SourceType2["INVENTORY"] = "INVENTORY";
  SourceType2["INVENTORY_LINE"] = "INVENTORY_LINE";
  SourceType2["MANUAL"] = "MANUAL";
  SourceType2["SUPPLIER_RETURN_ITEM"] = "SUPPLIER_RETURN_ITEM";
  return SourceType2;
})(SourceType || {});
var SOURCE_TYPE_LABELS = {
  ["GOODS_RECEIPT" /* GOODS_RECEIPT */]: "Reception",
  ["THEORETICAL_CONSUMPTION" /* THEORETICAL_CONSUMPTION */]: "Consommation theorique",
  ["LOSS" /* LOSS */]: "Perte",
  ["TRANSFER" /* TRANSFER */]: "Transfert",
  ["INVENTORY" /* INVENTORY */]: "Inventaire",
  ["INVENTORY_LINE" /* INVENTORY_LINE */]: "Inventaire",
  ["MANUAL" /* MANUAL */]: "Manuel",
  ["SUPPLIER_RETURN_ITEM" /* SUPPLIER_RETURN_ITEM */]: "Retour fournisseur"
};
function isInboundMovement(type) {
  return type === "IN" /* IN */;
}
function isOutboundMovement(type) {
  return type === "OUT" /* OUT */;
}
function getQuantitySign(type) {
  return type === "IN" /* IN */ ? 1 : -1;
}

// src/enums/inventory.enum.ts
var InventoryType = /* @__PURE__ */ ((InventoryType2) => {
  InventoryType2["FULL"] = "FULL";
  InventoryType2["BY_CATEGORY"] = "BY_CATEGORY";
  InventoryType2["BY_SELECTION"] = "BY_SELECTION";
  InventoryType2["BY_LIST"] = "BY_LIST";
  return InventoryType2;
})(InventoryType || {});
var INVENTORY_TYPE_LABELS = {
  ["FULL" /* FULL */]: "Complet",
  ["BY_CATEGORY" /* BY_CATEGORY */]: "Par categories",
  ["BY_SELECTION" /* BY_SELECTION */]: "Selection",
  ["BY_LIST" /* BY_LIST */]: "Liste pr\xE9d\xE9finie"
};
var INVENTORY_TYPE_COLORS = {
  ["FULL" /* FULL */]: "gray",
  ["BY_CATEGORY" /* BY_CATEGORY */]: "blue",
  ["BY_SELECTION" /* BY_SELECTION */]: "orange",
  ["BY_LIST" /* BY_LIST */]: "purple"
};
var INVENTORY_STATUS_LABELS = {
  ["DRAFT" /* DRAFT */]: "Brouillon",
  ["IN_PROGRESS" /* IN_PROGRESS */]: "En cours",
  ["VALIDATED" /* VALIDATED */]: "Valide",
  ["CANCELLED" /* CANCELLED */]: "Annule"
};
var INVENTORY_STATUS_COLORS = {
  ["DRAFT" /* DRAFT */]: "gray",
  ["IN_PROGRESS" /* IN_PROGRESS */]: "orange",
  ["VALIDATED" /* VALIDATED */]: "green",
  ["CANCELLED" /* CANCELLED */]: "red"
};

// src/enums/loss-reason.enum.ts
var LossReason = /* @__PURE__ */ ((LossReason2) => {
  LossReason2["DLC"] = "DLC";
  LossReason2["CASSE"] = "CASSE";
  LossReason2["VOL"] = "VOL";
  LossReason2["PRODUCTION"] = "PRODUCTION";
  LossReason2["AUTRE"] = "AUTRE";
  return LossReason2;
})(LossReason || {});
var LOSS_REASON_LABELS = {
  ["DLC" /* DLC */]: "DLC depassee",
  ["CASSE" /* CASSE */]: "Casse",
  ["VOL" /* VOL */]: "Vol",
  ["PRODUCTION" /* PRODUCTION */]: "Production",
  ["AUTRE" /* AUTRE */]: "Autre"
};
var LOSS_REASON_COLORS = {
  ["DLC" /* DLC */]: "danger",
  ["CASSE" /* CASSE */]: "warning",
  ["VOL" /* VOL */]: "danger",
  ["PRODUCTION" /* PRODUCTION */]: "info",
  ["AUTRE" /* AUTRE */]: "neutral"
};
var LOSS_REASON_OPTIONS = [
  { value: "DLC" /* DLC */, label: LOSS_REASON_LABELS["DLC" /* DLC */] },
  { value: "CASSE" /* CASSE */, label: LOSS_REASON_LABELS["CASSE" /* CASSE */] },
  { value: "VOL" /* VOL */, label: LOSS_REASON_LABELS["VOL" /* VOL */] },
  { value: "PRODUCTION" /* PRODUCTION */, label: LOSS_REASON_LABELS["PRODUCTION" /* PRODUCTION */] },
  { value: "AUTRE" /* AUTRE */, label: LOSS_REASON_LABELS["AUTRE" /* AUTRE */] }
];

// src/enums/transfer-status.enum.ts
var TransferStatus = /* @__PURE__ */ ((TransferStatus2) => {
  TransferStatus2["DRAFT"] = "DRAFT";
  TransferStatus2["PENDING"] = "PENDING";
  TransferStatus2["SHIPPED"] = "SHIPPED";
  TransferStatus2["RECEIVED"] = "RECEIVED";
  TransferStatus2["DISPUTED"] = "DISPUTED";
  TransferStatus2["CANCELLED"] = "CANCELLED";
  return TransferStatus2;
})(TransferStatus || {});
var TRANSFER_STATUS_LABELS = {
  ["DRAFT" /* DRAFT */]: "Brouillon",
  ["PENDING" /* PENDING */]: "En attente",
  ["SHIPPED" /* SHIPPED */]: "Expedie",
  ["RECEIVED" /* RECEIVED */]: "Recu",
  ["DISPUTED" /* DISPUTED */]: "Litige",
  ["CANCELLED" /* CANCELLED */]: "Annule"
};
var TRANSFER_STATUS_LABELS_REQUESTER = {
  ["DRAFT" /* DRAFT */]: "Brouillon",
  ["PENDING" /* PENDING */]: "En attente d'expedition",
  ["SHIPPED" /* SHIPPED */]: "A receptionner",
  ["RECEIVED" /* RECEIVED */]: "Receptionne",
  ["DISPUTED" /* DISPUTED */]: "Litige a traiter",
  ["CANCELLED" /* CANCELLED */]: "Annule"
};
var TRANSFER_STATUS_LABELS_SUPPLIER = {
  ["DRAFT" /* DRAFT */]: "Brouillon",
  // Ne devrait pas voir
  ["PENDING" /* PENDING */]: "A expedier",
  ["SHIPPED" /* SHIPPED */]: "Expedie",
  ["RECEIVED" /* RECEIVED */]: "Livre",
  ["DISPUTED" /* DISPUTED */]: "Litige signale",
  ["CANCELLED" /* CANCELLED */]: "Annule"
};
function getTransferStatusLabel(status, isRequester) {
  return isRequester ? TRANSFER_STATUS_LABELS_REQUESTER[status] : TRANSFER_STATUS_LABELS_SUPPLIER[status];
}
var TRANSFER_STATUS_COLORS = {
  ["DRAFT" /* DRAFT */]: "neutral",
  ["PENDING" /* PENDING */]: "warning",
  ["SHIPPED" /* SHIPPED */]: "info",
  ["RECEIVED" /* RECEIVED */]: "success",
  ["DISPUTED" /* DISPUTED */]: "warning",
  ["CANCELLED" /* CANCELLED */]: "danger"
};
var TRANSFER_STATUS_OPTIONS = [
  { value: "DRAFT" /* DRAFT */, label: TRANSFER_STATUS_LABELS["DRAFT" /* DRAFT */] },
  { value: "PENDING" /* PENDING */, label: TRANSFER_STATUS_LABELS["PENDING" /* PENDING */] },
  { value: "SHIPPED" /* SHIPPED */, label: TRANSFER_STATUS_LABELS["SHIPPED" /* SHIPPED */] },
  { value: "RECEIVED" /* RECEIVED */, label: TRANSFER_STATUS_LABELS["RECEIVED" /* RECEIVED */] },
  { value: "DISPUTED" /* DISPUTED */, label: TRANSFER_STATUS_LABELS["DISPUTED" /* DISPUTED */] },
  { value: "CANCELLED" /* CANCELLED */, label: TRANSFER_STATUS_LABELS["CANCELLED" /* CANCELLED */] }
];
function canEditTransfer(status) {
  return status === "DRAFT" /* DRAFT */;
}
function canSubmitTransfer(status) {
  return status === "DRAFT" /* DRAFT */;
}
function canCancelTransfer(status) {
  return status === "DRAFT" /* DRAFT */ || status === "PENDING" /* PENDING */ || status === "SHIPPED" /* SHIPPED */;
}
function canShipTransfer(status) {
  return status === "PENDING" /* PENDING */;
}
function canReceiveTransfer(status) {
  return status === "SHIPPED" /* SHIPPED */;
}

// src/enums/document.enum.ts
var DocumentEntityType = /* @__PURE__ */ ((DocumentEntityType2) => {
  DocumentEntityType2["PURCHASE_ORDER"] = "PURCHASE_ORDER";
  DocumentEntityType2["GOODS_RECEIPT"] = "GOODS_RECEIPT";
  DocumentEntityType2["TRANSFER"] = "TRANSFER";
  DocumentEntityType2["INVENTORY"] = "INVENTORY";
  DocumentEntityType2["SUPPLIER_RETURN"] = "SUPPLIER_RETURN";
  return DocumentEntityType2;
})(DocumentEntityType || {});
var DocumentType = /* @__PURE__ */ ((DocumentType2) => {
  DocumentType2["PURCHASE_ORDER_PDF"] = "PURCHASE_ORDER_PDF";
  DocumentType2["TRANSFER_ORDER_PDF"] = "TRANSFER_ORDER_PDF";
  DocumentType2["TRANSFER_DELIVERY_NOTE"] = "TRANSFER_DELIVERY_NOTE";
  DocumentType2["TRANSFER_INVOICE"] = "TRANSFER_INVOICE";
  DocumentType2["GOODS_RECEIPT_BL_SUPPLIER"] = "GOODS_RECEIPT_BL_SUPPLIER";
  DocumentType2["GOODS_RECEIPT_INVOICE_SUPPLIER"] = "GOODS_RECEIPT_INVOICE_SUPPLIER";
  DocumentType2["GOODS_RECEIPT_PDF"] = "GOODS_RECEIPT_PDF";
  DocumentType2["INVENTORY_COMPLETED_PDF"] = "INVENTORY_COMPLETED_PDF";
  DocumentType2["INVENTORY_COMPLETED_EXCEL"] = "INVENTORY_COMPLETED_EXCEL";
  DocumentType2["SUPPLIER_RETURN_BL"] = "SUPPLIER_RETURN_BL";
  return DocumentType2;
})(DocumentType || {});
var DOCUMENT_TYPE_LABELS = {
  ["PURCHASE_ORDER_PDF" /* PURCHASE_ORDER_PDF */]: "Bon de commande",
  ["TRANSFER_ORDER_PDF" /* TRANSFER_ORDER_PDF */]: "Bon de commande interne",
  ["TRANSFER_DELIVERY_NOTE" /* TRANSFER_DELIVERY_NOTE */]: "Bon de livraison",
  ["TRANSFER_INVOICE" /* TRANSFER_INVOICE */]: "Facture transfert",
  ["GOODS_RECEIPT_BL_SUPPLIER" /* GOODS_RECEIPT_BL_SUPPLIER */]: "BL fournisseur",
  ["GOODS_RECEIPT_INVOICE_SUPPLIER" /* GOODS_RECEIPT_INVOICE_SUPPLIER */]: "Facture fournisseur",
  ["GOODS_RECEIPT_PDF" /* GOODS_RECEIPT_PDF */]: "Bon de reception",
  ["INVENTORY_COMPLETED_PDF" /* INVENTORY_COMPLETED_PDF */]: "Inventaire compl\xE9t\xE9",
  ["INVENTORY_COMPLETED_EXCEL" /* INVENTORY_COMPLETED_EXCEL */]: "Inventaire compl\xE9t\xE9 (Excel)",
  ["SUPPLIER_RETURN_BL" /* SUPPLIER_RETURN_BL */]: "BL retour fournisseur"
};

// src/enums/supplier-return.enum.ts
var SupplierReturnStatus = /* @__PURE__ */ ((SupplierReturnStatus2) => {
  SupplierReturnStatus2["DRAFT"] = "DRAFT";
  SupplierReturnStatus2["VALIDATED"] = "VALIDATED";
  SupplierReturnStatus2["CANCELLED"] = "CANCELLED";
  return SupplierReturnStatus2;
})(SupplierReturnStatus || {});
var SUPPLIER_RETURN_STATUS_LABELS = {
  ["DRAFT" /* DRAFT */]: "Brouillon",
  ["VALIDATED" /* VALIDATED */]: "Valide",
  ["CANCELLED" /* CANCELLED */]: "Annule"
};
var SUPPLIER_RETURN_STATUS_COLORS = {
  ["DRAFT" /* DRAFT */]: "gray",
  ["VALIDATED" /* VALIDATED */]: "green",
  ["CANCELLED" /* CANCELLED */]: "red"
};
var SupplierReturnItemReason = /* @__PURE__ */ ((SupplierReturnItemReason2) => {
  SupplierReturnItemReason2["DAMAGED"] = "DAMAGED";
  SupplierReturnItemReason2["EXPIRED"] = "EXPIRED";
  SupplierReturnItemReason2["WRONG_PRODUCT"] = "WRONG_PRODUCT";
  SupplierReturnItemReason2["WRONG_QUANTITY"] = "WRONG_QUANTITY";
  SupplierReturnItemReason2["QUALITY_ISSUE"] = "QUALITY_ISSUE";
  SupplierReturnItemReason2["OTHER"] = "OTHER";
  return SupplierReturnItemReason2;
})(SupplierReturnItemReason || {});
var SUPPLIER_RETURN_ITEM_REASON_LABELS = {
  ["DAMAGED" /* DAMAGED */]: "Produit endommage",
  ["EXPIRED" /* EXPIRED */]: "DLC depassee / proche",
  ["WRONG_PRODUCT" /* WRONG_PRODUCT */]: "Mauvais produit",
  ["WRONG_QUANTITY" /* WRONG_QUANTITY */]: "Quantite excedentaire",
  ["QUALITY_ISSUE" /* QUALITY_ISSUE */]: "Probleme qualite",
  ["OTHER" /* OTHER */]: "Autre"
};

// src/types/pos-sales/index.ts
var saleTypeLabels = {
  STANDARD: "Standard",
  PROMO: "Promo",
  EMPLOYEE_MEAL: "Repas employe",
  OFFERED: "Offert",
  WASTE: "Casse"
};
var importSourceLabels = {
  MANUAL: "Manuel",
  API_BILL: "API Bill",
  SEED: "Seed",
  MANUAL_RESYNC: "Re-synchro manuelle",
  GAP_SWEEP: "Rattrapage automatique"
};

// src/types/sse.types.ts
var SalesImportStepLabels = {
  init: "Demarrage de l'import...",
  fetch_receipts: "Recuperation des tickets de caisse...",
  load_mappings: "Comparaison des SKU...",
  prepare_data: "Preparation des donnees a importer...",
  bulk_insert: "Import des tickets...",
  load_recipe_data: "Chargement des recettes...",
  compute_snapshots: "Association des recettes aux lignes produit...",
  compute_consumptions: "Calcul des consommations theoriques...",
  finalize: "Finalisation...",
  done: "Termine"
};
var MarginReportStepLabels = {
  loading_products: "Chargement des produits...",
  loading_sales: "Calcul du chiffre d'affaires...",
  loading_stock: "Calcul des stocks...",
  loading_consumption: "Calcul des consommations theoriques...",
  loading_losses: "Calcul des pertes...",
  calculating_bulk: "Analyse bulk / non-bulk...",
  assembling: "Assemblage du rapport...",
  done: "Termine"
};
var createPackagingSchema = z.object({
  name: z.string().min(1, "Le nom est requis").max(100, "Le nom est trop long"),
  unitId: z.string().uuid("L'unite est requise"),
  quantity: z.number().positive("La quantite doit etre positive").default(1)
});
var updatePackagingSchema = z.object({
  name: z.string().min(1, "Le nom est requis").max(100, "Le nom est trop long").optional(),
  unitId: z.string().uuid("L'unite est requise").optional(),
  quantity: z.number().positive("La quantite doit etre positive").optional(),
  isActive: z.boolean().optional()
});

// src/types/current-stock.types.ts
var STOCK_STATUS_LABELS = {
  ok: "OK",
  low: "Bas",
  critical: "Critique",
  overstocked: "Surstock"
};
var STOCK_STATUS_COLORS = {
  ok: "success",
  low: "warning",
  critical: "danger",
  overstocked: "info"
};
var BREAKDOWN_REASON_LABELS = {
  RECEPTION: "Receptions",
  POS_SALE: "Ventes POS",
  THEORETICAL_CONSUMPTION: "Consommations theoriques",
  LOSS: "Pertes",
  TRANSFER_IN: "Transferts entrants",
  TRANSFER_OUT: "Transferts sortants",
  ADJUSTMENT: "Ajustements",
  INVENTORY: "Ajustements inventaire"
};

// src/types/loss.types.ts
var LOSS_TYPE_LABELS = {
  RAW: "Brut",
  FINISHED: "Fini",
  EXCEPTIONAL: "Exceptionnel"
};
var LOSS_TYPE_COLORS = {
  RAW: "neutral",
  FINISHED: "info",
  EXCEPTIONAL: "warning"
};
var LOSS_TYPE_OPTIONS = [
  { value: "RAW", label: LOSS_TYPE_LABELS.RAW },
  { value: "FINISHED", label: LOSS_TYPE_LABELS.FINISHED },
  { value: "EXCEPTIONAL", label: LOSS_TYPE_LABELS.EXCEPTIONAL }
];

// src/types/transfer.types.ts
function isSourceSite(transfer, currentSiteId) {
  return transfer.sourceSiteId === currentSiteId;
}
function isDestinationSite(transfer, currentSiteId) {
  return transfer.destinationSiteId === currentSiteId;
}
function getSiteRole(transfer, currentSiteId) {
  if (transfer.sourceSiteId === currentSiteId) return "source";
  if (transfer.destinationSiteId === currentSiteId) return "destination";
  return null;
}

// src/types/admin.types.ts
var PosSyncStatus = {
  SUCCESS: "SUCCESS",
  ERROR: "ERROR",
  PARTIAL: "PARTIAL"
};
var PosProvider = {
  BILL: "BILL"
};
var createCategorySchema = z.object({
  name: z.string().min(2, "Le nom doit contenir au moins 2 caracteres").max(100, "Le nom ne doit pas depasser 100 caracteres"),
  code: z.string().min(2).max(50).optional(),
  parentId: z.string().uuid().nullable().optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "La couleur doit etre au format hexadecimal (#RRGGBB)").nullable().optional()
});
var updateCategorySchema = z.object({
  name: z.string().min(2, "Le nom doit contenir au moins 2 caracteres").max(100, "Le nom ne doit pas depasser 100 caracteres").optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "La couleur doit etre au format hexadecimal (#RRGGBB)").nullable().optional(),
  sortOrder: z.number().int().min(0).optional()
});
var moveCategorySchema = z.object({
  parentId: z.string().uuid().nullable(),
  position: z.number().int().min(0).optional()
});
var listCategoriesQuerySchema = z.object({
  flat: z.enum(["true", "false"]).optional().transform((val) => val === "true"),
  includeInactive: z.enum(["true", "false"]).optional().transform((val) => val === "true")
});
var UUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
var uuidLike = (msg = "ID invalide") => z.string().regex(UUID_REGEX, msg);

// src/constants/pagination.ts
var PAGE_SIZE_DEFAULT = 20;
var PAGE_SIZE_MAX = 100;
var EXPORT_LIMIT_MAX = 1e4;

// src/validators/raw-products.validators.ts
var createRawProductSchema = z.object({
  sku: z.string().min(2, "SKU: minimum 2 caracteres").max(50, "SKU: maximum 50 caracteres").regex(/^[A-Za-z0-9\-_]+$/, "SKU: caracteres alphanumeriques et tirets uniquement"),
  name: z.string().min(2, "Nom: minimum 2 caracteres").max(200, "Nom: maximum 200 caracteres"),
  description: z.string().max(1e3).nullable().optional(),
  barcode: z.string().regex(/^\d{8,14}$/, "Code-barres: 8 a 14 chiffres").nullable().optional(),
  categoryId: z.string().uuid("Categorie: ID invalide"),
  marketType: z.enum(["FOOD", "FOOD_IND", "NON_FOOD", "NON_FOOD_IND", "MAINTENANCE"], { error: "Type de marche invalide" }),
  recipeUnitId: uuidLike("Unite recette: ID invalide"),
  purchaseVatRate: z.number().min(0).max(100).default(5.5),
  saleVatRate: z.number().min(0).max(100).default(5.5),
  allergens: z.array(z.string()).optional().default([]),
  nutritionalInfo: z.record(z.string(), z.unknown()).nullable().optional(),
  minStock: z.number().min(0).nullable().optional(),
  maxStock: z.number().min(0).nullable().optional(),
  isBulk: z.boolean().optional().default(false)
}).refine(
  (data) => !data.minStock || !data.maxStock || data.maxStock > data.minStock,
  { message: "Le stock maximum doit etre superieur au stock minimum", path: ["maxStock"] }
);
var updateRawProductSchema = z.object({
  name: z.string().min(2).max(200).optional(),
  description: z.string().max(1e3).nullable().optional(),
  barcode: z.string().regex(/^\d{8,14}$/).nullable().optional(),
  categoryId: z.string().uuid().optional(),
  // Modifiable : change le classement du produit dans le rapport de marge, y compris sur les periodes passees
  marketType: z.enum(["FOOD", "FOOD_IND", "NON_FOOD", "NON_FOOD_IND", "MAINTENANCE"], { error: "Type de marche invalide" }).optional(),
  recipeUnitId: uuidLike("Unite recette: ID invalide").optional(),
  purchaseVatRate: z.number().min(0).max(100).optional(),
  saleVatRate: z.number().min(0).max(100).optional(),
  allergens: z.array(z.string()).optional(),
  nutritionalInfo: z.record(z.string(), z.unknown()).nullable().optional(),
  minStock: z.number().min(0).nullable().optional(),
  maxStock: z.number().min(0).nullable().optional(),
  isBulk: z.boolean().optional(),
  isActive: z.boolean().optional()
}).refine(
  (data) => !data.minStock || !data.maxStock || data.maxStock > data.minStock,
  { message: "Le stock maximum doit etre superieur au stock minimum", path: ["maxStock"] }
);
var queryRawProductsSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(EXPORT_LIMIT_MAX).default(20),
  search: z.string().optional(),
  categoryId: z.string().uuid().optional(),
  // Support both single marketType and comma-separated marketTypes
  marketType: z.enum(["FOOD", "FOOD_IND", "NON_FOOD", "NON_FOOD_IND", "MAINTENANCE"]).optional(),
  marketTypes: z.string().optional().transform((val) => {
    if (!val) return void 0;
    const validTypes = ["FOOD", "FOOD_IND", "NON_FOOD", "NON_FOOD_IND", "MAINTENANCE"];
    const types = val.split(",").filter((t) => validTypes.includes(t));
    return types.length > 0 ? types : void 0;
  }),
  isActive: z.string().optional().transform((val) => val === "false" ? false : true),
  sortBy: z.enum(["name", "sku", "createdAt", "updatedAt"]).default("name"),
  sortOrder: z.enum(["asc", "desc"]).default("asc")
});
var listUnitsQuerySchema = z.object({
  groupByType: z.enum(["true", "false"]).optional().transform((val) => val === "true"),
  type: z.enum(["MASS", "VOLUME", "PIECE"]).optional()
});
var unitTypeSchema = z.enum(["MASS", "VOLUME", "PIECE"]);
var convertQuerySchema = z.object({
  from: z.string().min(1, 'Parametre "from" requis'),
  to: z.string().min(1, 'Parametre "to" requis'),
  value: z.coerce.number().positive().optional().default(1)
});
var queryPosProductsSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(1e3).default(20),
  search: z.string().optional(),
  category: z.string().optional(),
  type: z.enum([PosProductType.PRODUCT, PosProductType.OPTION]).optional(),
  syncStatus: z.enum([SyncStatus.SYNCED, SyncStatus.PENDING, SyncStatus.ERROR]).optional(),
  sortBy: z.enum([
    PosProductSortField.NAME,
    PosProductSortField.SKU,
    PosProductSortField.TYPE,
    PosProductSortField.POS_CATEGORY,
    PosProductSortField.SELL_PRICE,
    PosProductSortField.SYNC_STATUS
  ]).default(PosProductSortField.NAME),
  sortOrder: z.enum([SortOrder.ASC, SortOrder.DESC]).default(SortOrder.ASC),
  missingRecipeOnly: z.preprocess(
    (val) => val === "true" || val === true,
    z.boolean().optional()
  )
});
var syncPosProductsSchema = z.object({
  force: z.boolean().optional().default(false)
});
var recipeIngredientInputSchema = z.object({
  rawProductId: z.string().uuid("Produit brut: ID invalide"),
  quantity: z.number().positive("Quantite: doit etre superieure a 0").max(999999, "Quantite: valeur trop grande"),
  notes: z.string().max(500, "Notes: maximum 500 caracteres").nullable().optional(),
  sortOrder: z.number().int().min(0).optional()
});
var createRecipeSchema = z.object({
  name: z.string().min(2, "Nom: minimum 2 caracteres").max(200, "Nom: maximum 200 caracteres"),
  description: z.string().max(1e3, "Description: maximum 1000 caracteres").nullable().optional(),
  posProductIds: z.array(z.string().uuid("Produit POS: ID invalide")).optional().default([]),
  yield: z.number().positive("Rendement: doit etre superieur a 0").nullable().optional(),
  yieldUnitId: z.string().uuid("Unite de rendement: ID invalide").nullable().optional(),
  ingredients: z.array(recipeIngredientInputSchema).default([])
}).refine(
  (data) => {
    if (data.yieldUnitId && !data.yield) {
      return false;
    }
    return true;
  },
  { message: "Rendement: si une unite est specifiee, le rendement doit aussi etre fourni" }
);
var updateRecipeSchema = z.object({
  name: z.string().min(2).max(200).optional(),
  description: z.string().max(1e3).nullable().optional(),
  posProductIds: z.array(z.string().uuid()).optional(),
  yield: z.number().positive().nullable().optional(),
  yieldUnitId: z.string().uuid().nullable().optional(),
  ingredients: z.array(recipeIngredientInputSchema).optional()
});
var addIngredientSchema = z.object({
  rawProductId: z.string().uuid("Produit brut: ID invalide"),
  quantity: z.number().positive("Quantite: doit etre superieure a 0"),
  notes: z.string().max(500).nullable().optional(),
  sortOrder: z.number().int().min(0).optional()
});
var updateIngredientSchema = z.object({
  quantity: z.number().positive("Quantite: doit etre superieure a 0").optional(),
  notes: z.string().max(500).nullable().optional(),
  sortOrder: z.number().int().min(0).optional()
});
var reorderIngredientsSchema = z.object({
  ingredientIds: z.array(z.string().uuid()).min(1, "Au moins 1 ingredient requis")
});
var duplicateRecipeSchema = z.object({
  name: z.string().min(2).max(200).optional()
});
var publishRecipeSchema = z.object({
  posProductIds: z.array(z.string().uuid("Produit POS: ID invalide")).optional()
});
var queryRecipesSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(EXPORT_LIMIT_MAX).default(20),
  search: z.string().optional(),
  hasProduct: z.string().optional().transform((val) => {
    if (val === "true") return true;
    if (val === "false") return false;
    return void 0;
  }),
  posProductId: z.string().uuid().optional(),
  rawProductId: z.string().uuid().optional(),
  posCategory: z.string().optional(),
  includeArchived: z.string().optional().transform((val) => val === "true"),
  includeDrafts: z.string().optional().transform((val) => {
    if (val === "true") return true;
    if (val === "false") return false;
    return void 0;
  }),
  hasDraft: z.string().optional().transform((val) => val === "true"),
  sortBy: z.enum(["name", "createdAt", "updatedAt", "version", "theoreticalCost"]).default("name"),
  sortOrder: z.enum(["asc", "desc"]).default("asc")
});
var queryRecipeAtDateSchema = z.object({
  posProductId: z.string().uuid("Produit POS: ID invalide"),
  date: isoDateTimeString
});
var phoneSchema = z.string().max(20, "Telephone: maximum 20 caracteres").regex(
  /^[+]?[\d\s.\-()]+$/,
  "Telephone: format invalide (chiffres, espaces, +, -, . autorises)"
).refine(
  (val) => val.replace(/\D/g, "").length >= 8,
  "Telephone: minimum 8 chiffres"
);
var createSupplierSchema = z.object({
  name: z.string().min(2, "Nom: minimum 2 caracteres").max(30, "Nom: maximum 30 caracteres"),
  siret: z.string().regex(/^\d{14}$/, "SIRET: 14 chiffres exactement").nullable().optional(),
  code: z.string().max(50, "Code: maximum 50 caracteres").nullable().optional(),
  email: z.string().email("Email invalide").nullable().optional(),
  phone: phoneSchema.nullable().optional(),
  addressLine1: z.string().max(200, "Adresse: maximum 200 caracteres").nullable().optional(),
  addressLine2: z.string().max(200, "Complement: maximum 200 caracteres").nullable().optional(),
  postalCode: z.string().max(10, "Code postal: maximum 10 caracteres").nullable().optional(),
  city: z.string().max(100, "Ville: maximum 100 caracteres").nullable().optional(),
  country: z.string().max(100, "Pays: maximum 100 caracteres").nullable().optional().default("France"),
  isActive: z.boolean().optional().default(true),
  accessScope: z.nativeEnum(SupplierAccessScope).optional().default(SupplierAccessScope.BOTH)
});
var updateSupplierSchema = z.object({
  name: z.string().min(2).max(30).optional(),
  siret: z.string().regex(/^\d{14}$/).nullable().optional(),
  code: z.string().max(50).nullable().optional(),
  email: z.string().email().nullable().optional(),
  phone: phoneSchema.nullable().optional(),
  addressLine1: z.string().max(200).nullable().optional(),
  addressLine2: z.string().max(200).nullable().optional(),
  postalCode: z.string().max(10).nullable().optional(),
  city: z.string().max(100).nullable().optional(),
  country: z.string().max(100).nullable().optional(),
  isActive: z.boolean().optional(),
  accessScope: z.nativeEnum(SupplierAccessScope).optional()
});
var querySuppliersSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(500).default(20),
  search: z.string().optional(),
  isActive: z.string().optional().transform((val) => {
    if (val === "false") return false;
    if (val === "true") return true;
    return void 0;
  }),
  sortBy: z.enum(["name", "createdAt", "updatedAt"]).default("name"),
  sortOrder: z.enum(["asc", "desc"]).default("asc")
});
var createCatalogItemSchema = z.object({
  rawProductId: z.string().uuid("Produit brut: ID invalide"),
  packagingId: z.string().uuid("Conditionnement: ID invalide"),
  conversionToRecipeUnit: z.number().positive("Conversion: doit etre positive").max(99999999, "Conversion: valeur trop grande (max: 99999999)"),
  priceExclTax: z.number().int("Prix: doit etre en centimes").min(0, "Prix: doit etre positif").max(1e6, "Prix: max 10 000 (changez d'unite si besoin)"),
  supplierRef: z.string().max(100).nullable().optional()
});
var updateCatalogItemSchema = z.object({
  packagingId: z.string().uuid("Conditionnement: ID invalide").optional(),
  conversionToRecipeUnit: z.number().positive("Conversion: doit etre positive").max(99999999, "Conversion: valeur trop grande (max: 99999999)").optional(),
  supplierRef: z.string().max(100).nullable().optional(),
  isActive: z.boolean().optional()
});
var updateCatalogItemPriceSchema = z.object({
  priceExclTax: z.number().int("Prix: doit etre en centimes").min(0, "Prix: doit etre positif").max(1e6, "Prix: max 10 000 (changez d'unite si besoin)")
});
var queryCatalogItemsSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(EXPORT_LIMIT_MAX).default(50),
  search: z.string().optional(),
  isActive: z.string().optional().transform((val) => {
    if (val === "false") return false;
    if (val === "true") return true;
    return void 0;
  }),
  sortBy: z.enum(["rawProductName", "priceExclTax", "createdAt"]).default("rawProductName"),
  sortOrder: z.enum(["asc", "desc"]).default("asc")
});
var orderItemSchema = z.object({
  catalogItemId: z.string().uuid("Reference catalogue: ID invalide"),
  quantity: z.number().int("Quantite: doit etre un entier").positive("Quantite: doit etre positive").max(999999, "Quantite: valeur trop grande")
});
var createPurchaseOrderSchema = z.object({
  supplierId: z.string().uuid("Fournisseur: ID invalide"),
  expectedDate: isoDateTimeString.nullable().optional(),
  notes: z.string().max(1e3, "Notes: maximum 1000 caracteres").nullable().optional(),
  items: z.array(orderItemSchema).optional().default([])
});
var updatePurchaseOrderSchema = z.object({
  expectedDate: isoDateTimeString.nullable().optional(),
  notes: z.string().max(1e3).nullable().optional(),
  items: z.array(orderItemSchema).optional()
});
var addOrderItemSchema = orderItemSchema;
var updateOrderItemSchema = z.object({
  quantity: z.number().int().positive().max(999999).optional(),
  catalogItemId: z.string().uuid().optional()
});
var VALID_STATUSES = ["DRAFT", "SENT", "PARTIALLY_RECEIVED", "RECEIVED", "CANCELLED"];
var queryPurchaseOrdersSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(EXPORT_LIMIT_MAX).default(20),
  supplierId: z.string().uuid().optional(),
  // Comma-separated list of statuses (e.g., "SENT,PARTIALLY_RECEIVED")
  status: z.string().optional().transform((val) => {
    if (!val) return void 0;
    const statuses = val.split(",").map((s) => s.trim());
    const validStatuses = statuses.filter((s) => VALID_STATUSES.includes(s));
    return validStatuses.length > 0 ? validStatuses : void 0;
  }),
  startDate: dateOrDateTimeString.optional(),
  endDate: dateOrDateTimeString.optional(),
  sortBy: z.enum(["orderDate", "orderNumber", "totalExclTax", "createdAt"]).default("orderDate"),
  sortOrder: z.enum(["asc", "desc"]).default("desc")
});
var goodsReceiptItemSchema = z.object({
  purchaseOrderItemId: z.string().uuid("Ligne commande: ID invalide").nullable().optional(),
  catalogItemId: z.string().uuid("Reference catalogue: ID invalide").nullable().optional(),
  rawProductId: z.string().uuid("Produit: ID invalide"),
  quantityReceived: z.number().int("Quantite: doit etre un entier").min(0, "Quantite: doit etre positive ou nulle").max(999999, "Quantite: valeur trop grande"),
  unitPriceExclTax: z.number().int("Prix: doit etre en centimes (entier)").min(0, "Prix: doit etre positif ou nul").max(1e6, "Prix unitaire: max 10 000 (changez d'unite si besoin)"),
  varianceReason: z.nativeEnum(VarianceReason).nullable().optional()
});
var createGoodsReceiptSchema = z.object({
  purchaseOrderId: z.string().uuid("Commande: ID invalide").nullable().optional(),
  supplierId: z.string().uuid("Fournisseur: ID invalide"),
  receiptDate: isoDateTimeString,
  isDefinitive: z.boolean().default(true),
  supplierInvoiceRef: z.string().max(100, "Reference facture: max 100 caracteres").nullable().optional(),
  notes: z.string().max(1e3, "Notes: max 1000 caracteres").nullable().optional(),
  items: z.array(goodsReceiptItemSchema).min(1, "Au moins une ligne requise")
});
var updateGoodsReceiptSchema = z.object({
  receiptDate: isoDateTimeString.optional(),
  supplierInvoiceRef: z.string().max(100).nullable().optional(),
  blNumber: z.string().max(100, "N\xB0 BL: max 100 caracteres").nullable().optional(),
  notes: z.string().max(1e3).nullable().optional()
});
var updateGoodsReceiptItemSchema = z.object({
  quantityReceived: z.number().int().min(0).max(999999).optional(),
  unitPriceExclTax: z.number().int().min(0).max(2e6).optional(),
  varianceReason: z.nativeEnum(VarianceReason).nullable().optional()
});
var initReceiptFromOrderSchema = z.object({
  purchaseOrderId: z.string().uuid("Commande: ID invalide"),
  receiptDate: isoDateTimeString.optional()
});
var queryGoodsReceiptsSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(EXPORT_LIMIT_MAX).default(20),
  supplierId: z.string().uuid().optional(),
  status: z.nativeEnum(GoodsReceiptStatus).optional(),
  startDate: dateOrDateTimeString.optional(),
  endDate: dateOrDateTimeString.optional(),
  purchaseOrderId: z.string().uuid().optional(),
  sortBy: z.enum(["receiptDate", "receiptNumber", "totalExclTax", "createdAt"]).default("receiptDate"),
  sortOrder: z.enum(["asc", "desc"]).default("desc")
});
var queryPMPHistorySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(EXPORT_LIMIT_MAX).default(10)
});
var createInventorySchema = z.object({
  type: z.nativeEnum(InventoryType, { error: "Type d'inventaire invalide" }),
  name: z.string().max(200, "Nom: max 200 caracteres").nullable().optional(),
  notes: z.string().max(2e3, "Notes: max 2000 caracteres").nullable().optional(),
  categoryIds: z.array(z.string().uuid("Categorie: ID invalide")).optional(),
  rawProductIds: z.array(z.string().uuid("Produit: ID invalide")).optional(),
  inventoryListId: z.string().uuid("Liste: ID invalide").nullable().optional(),
  includeZeroStock: z.boolean().optional().default(true),
  // La limite passé/futur est validée côté service via validateEventDate,
  // qui applique la marge symétrique du role (cf backdating.service.ts).
  effectiveDate: dateOrDateTimeString.optional()
}).refine(
  (data) => {
    if (data.type === "BY_CATEGORY" /* BY_CATEGORY */) {
      return data.categoryIds && data.categoryIds.length > 0;
    }
    return true;
  },
  { message: "Au moins une categorie requise pour un inventaire par categories", path: ["categoryIds"] }
).refine(
  (data) => {
    if (data.type === "BY_SELECTION" /* BY_SELECTION */) {
      return data.rawProductIds && data.rawProductIds.length > 0;
    }
    return true;
  },
  { message: "Au moins un produit requis pour un inventaire par selection", path: ["rawProductIds"] }
).refine(
  (data) => {
    if (data.type === "BY_LIST" /* BY_LIST */) {
      return data.inventoryListId && data.inventoryListId.length > 0;
    }
    return true;
  },
  { message: "Une liste requise pour un inventaire par liste pr\xE9d\xE9finie", path: ["inventoryListId"] }
);
var updateInventorySchema = z.object({
  name: z.string().max(200, "Nom: max 200 caracteres").nullable().optional(),
  notes: z.string().max(2e3, "Notes: max 2000 caracteres").nullable().optional()
});
var cancelInventorySchema = z.object({
  reason: z.string().max(500, "Motif: max 500 caracteres").optional()
});
var inventoryLineInputSchema = z.object({
  packagingId: z.string().uuid("Packaging: ID invalide").nullable(),
  packagingQty: z.number().min(0, "Quantite: doit etre positive ou nulle").max(1e4, "Quantite: max 10 000"),
  conversionFactor: z.number().positive("Facteur de conversion: doit etre positif").max(99999999, "Facteur: valeur trop grande").nullable()
});
var saveProductLinesSchema = z.object({
  notes: z.string().max(500, "Notes: max 500 caracteres").optional(),
  lines: z.array(inventoryLineInputSchema).min(0).max(20, "Maximum 20 lignes par produit")
}).refine(
  (data) => {
    return data.lines.every(
      (line) => line.packagingId === null || line.conversionFactor !== null
    );
  },
  { message: "Les lignes packaging doivent avoir un facteur de conversion", path: ["lines"] }
);
var queryInventoriesSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(EXPORT_LIMIT_MAX).default(20),
  status: z.union([
    z.nativeEnum(InventoryStatus),
    z.array(z.nativeEnum(InventoryStatus))
  ]).optional().transform((val) => {
    if (!val) return void 0;
    return Array.isArray(val) ? val : [val];
  }),
  type: z.nativeEnum(InventoryType).optional(),
  search: z.string().optional(),
  startDate: dateOrDateTimeString.optional(),
  endDate: dateOrDateTimeString.optional(),
  sortBy: z.enum(["inventoryNumber", "createdAt", "validatedAt", "status"]).default("createdAt"),
  sortOrder: z.enum(["asc", "desc"]).default("desc")
});
var createInventoryListSchema = z.object({
  name: z.string().min(1, "Nom: obligatoire").max(100, "Nom: max 100 caracteres"),
  description: z.string().max(500, "Description: max 500 caracteres").nullable().optional(),
  rawProductIds: z.array(z.string().uuid("Produit: ID invalide")).max(500, "Max 500 produits").optional().default([])
});
var updateInventoryListSchema = z.object({
  name: z.string().min(1, "Nom: obligatoire").max(100, "Nom: max 100 caracteres").optional(),
  description: z.string().max(500, "Description: max 500 caracteres").nullable().optional(),
  isActive: z.boolean().optional()
});
var updateInventoryListItemsSchema = z.object({
  rawProductIds: z.array(z.string().uuid("Produit: ID invalide")).min(1, "Au moins un produit requis").max(500, "Max 500 produits")
});
var queryInventoryListsSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(EXPORT_LIMIT_MAX).default(20),
  search: z.string().optional(),
  isActive: z.coerce.boolean().optional(),
  sortBy: z.enum(["name", "createdAt", "itemCount"]).default("name"),
  sortOrder: z.enum(["asc", "desc"]).default("asc")
});
var createLossSchema = z.object({
  rawProductId: z.string().uuid("ID produit invalide"),
  quantity: z.number().positive("Quantite: doit etre positive").max(999999, "Quantite: valeur trop grande"),
  lossReason: z.nativeEnum(LossReason, { error: "Motif de perte invalide" }),
  lossType: z.enum(["RAW", "EXCEPTIONAL"]).optional().default("RAW"),
  eventDate: isoDateTimeString.optional(),
  notes: z.string().max(500, "Notes: max 500 caracteres").nullable().optional()
}).refine(
  (data) => {
    if (data.lossReason === "AUTRE" /* AUTRE */) {
      return data.notes && data.notes.trim().length > 0;
    }
    return true;
  },
  { message: 'Commentaire obligatoire pour le motif "Autre"', path: ["notes"] }
);
var queryLossesSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(EXPORT_LIMIT_MAX).default(20),
  lossReason: z.nativeEnum(LossReason).optional(),
  lossType: z.enum(["RAW", "FINISHED", "EXCEPTIONAL"]).optional(),
  rawProductId: z.string().uuid("ID produit invalide").optional(),
  search: z.string().max(100).optional(),
  startDate: dateOrDateTimeString.optional(),
  endDate: dateOrDateTimeString.optional(),
  period: z.enum(["today", "7days", "30days", "all"]).optional(),
  sortBy: z.enum(["eventDate", "quantity", "totalValue", "lossReason"]).default("eventDate"),
  sortOrder: z.enum(["asc", "desc"]).default("desc")
});
var queryLossKPIsSchema = z.object({
  startDate: dateOrDateTimeString.optional(),
  endDate: dateOrDateTimeString.optional()
});
var createFinishedProductLossSchema = z.object({
  posProductId: z.string().uuid("ID produit fini invalide"),
  quantity: z.number().positive("Quantite: doit etre positive").int("Quantite: doit etre un entier").max(9999, "Quantite: max 9999"),
  lossReason: z.nativeEnum(LossReason, { error: "Motif de perte invalide" }),
  eventDate: isoDateTimeString.optional(),
  notes: z.string().max(500, "Notes: max 500 caracteres").nullable().optional()
}).refine(
  (data) => {
    if (data.lossReason === "AUTRE" /* AUTRE */) {
      return data.notes && data.notes.trim().length > 0;
    }
    return true;
  },
  { message: 'Commentaire obligatoire pour le motif "Autre"', path: ["notes"] }
);
var previewFinishedProductLossSchema = z.object({
  posProductId: z.string().uuid("ID produit fini invalide"),
  quantity: z.number().positive("Quantite: doit etre positive").int("Quantite: doit etre un entier").max(9999, "Quantite: max 9999")
});
var createTransferLineSchema = z.object({
  rawProductId: z.string().uuid("ID produit invalide"),
  quantity: z.number().positive("Quantite: doit etre positive").max(999999, "Quantite: valeur trop grande"),
  notes: z.string().max(500, "Notes: max 500 caracteres").optional(),
  // Packaging optionnel (Sprint 22)
  packagingId: z.string().uuid("ID packaging invalide").optional(),
  packagingQty: z.number().positive("Quantite packaging: doit etre positive").max(1e3, "Quantite packaging: max 1000 colis").optional(),
  conversionFactor: z.number().positive("Facteur de conversion: doit etre positif").max(99999999, "Facteur: trop grand").optional()
});
var createTransferSchema = z.object({
  sourceSiteId: z.string().uuid("ID site fournisseur invalide"),
  expectedShipDate: isoDateTimeString.optional(),
  notes: z.string().max(1e3, "Notes: max 1000 caracteres").optional(),
  lines: z.array(createTransferLineSchema).min(1, "Au moins une ligne requise").max(100, "Maximum 100 lignes")
});
var updateTransferSchema = z.object({
  expectedShipDate: isoDateTimeString.nullable().optional(),
  notes: z.string().max(1e3, "Notes: max 1000 caracteres").nullable().optional(),
  lines: z.array(createTransferLineSchema).min(1, "Au moins une ligne requise").max(100, "Maximum 100 lignes").optional()
});
var shipTransferLineSchema = z.object({
  transferLineId: z.string().uuid("ID ligne invalide"),
  quantityShipped: z.number().nonnegative("Quantite: doit etre positive ou nulle").max(999999, "Quantite: valeur trop grande"),
  // Packaging optionnel (Sprint 22)
  packagingId: z.string().uuid("ID packaging invalide").optional(),
  packagingQty: z.number().positive("Quantite packaging: doit etre positive").max(1e3, "Quantite packaging: max 1000 colis").optional(),
  conversionFactor: z.number().positive("Facteur de conversion: doit etre positif").max(99999999, "Facteur: trop grand").optional()
});
var shipTransferSchema = z.object({
  lines: z.array(shipTransferLineSchema).optional(),
  notes: z.string().max(1e3, "Notes: max 1000 caracteres").optional(),
  shipDate: isoDateTimeString.optional()
});
var receiveTransferLineSchema = z.object({
  transferLineId: z.string().uuid("ID ligne invalide"),
  quantityReceived: z.number().nonnegative("Quantite: doit etre positive ou nulle").max(999999, "Quantite: valeur trop grande"),
  varianceReason: z.string().max(500, "Raison: max 500 caracteres").optional(),
  // Packaging optionnel (Sprint 22)
  packagingId: z.string().uuid("ID packaging invalide").optional(),
  packagingQty: z.number().positive("Quantite packaging: doit etre positive").max(1e3, "Quantite packaging: max 1000 colis").optional(),
  conversionFactor: z.number().positive("Facteur de conversion: doit etre positif").max(99999999, "Facteur: trop grand").optional()
});
var receiveTransferSchema = z.object({
  lines: z.array(receiveTransferLineSchema).min(1, "Au moins une ligne requise"),
  notes: z.string().max(1e3, "Notes: max 1000 caracteres").optional(),
  receiveDate: isoDateTimeString.optional()
});
var cancelTransferSchema = z.object({
  reason: z.string().min(1, "Raison obligatoire").max(500, "Raison: max 500 caracteres")
});
var queryTransfersSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(EXPORT_LIMIT_MAX).default(20),
  // Comma-separated list of statuses (e.g., "PENDING,SHIPPED")
  status: z.string().optional().transform((val) => {
    if (!val) return void 0;
    const validValues = Object.values(TransferStatus);
    const statuses = val.split(",").map((s) => s.trim()).filter((s) => validValues.includes(s));
    return statuses.length > 0 ? statuses : void 0;
  }),
  sourceSiteId: z.string().uuid("ID site invalide").optional(),
  startDate: dateOrDateTimeString.optional(),
  endDate: dateOrDateTimeString.optional(),
  search: z.string().max(100).optional(),
  sortBy: z.enum(["createdAt", "shippedAt", "receivedAt", "transferNumber", "totalValueCentimes"]).default("createdAt"),
  sortOrder: z.enum(["asc", "desc"]).default("desc"),
  role: z.enum(["my_requests", "to_process", "all"]).default("all")
});
var queryCurrentStockSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(EXPORT_LIMIT_MAX).default(20),
  search: z.string().optional(),
  categoryId: z.string().uuid("Categorie: ID invalide").optional(),
  marketType: z.nativeEnum(MarketType).optional(),
  lowStockOnly: z.string().optional().transform((val) => val === "true"),
  includeInactive: z.string().optional().transform((val) => val === "true"),
  sortBy: z.enum(["name", "sku", "currentStock", "lastMovementDate", "lastInventoryDate"]).default("name"),
  sortOrder: z.enum(["asc", "desc"]).default("asc"),
  date: dateOrDateTimeString.optional()
});
var queryStockBreakdownSchema = z.object({
  date: dateOrDateTimeString.optional()
});
var stockReportQuerySchema = z.object({
  dateFrom: dateOrDateTimeString,
  dateTo: dateOrDateTimeString,
  categoryId: z.string().uuid().optional(),
  marketType: z.nativeEnum(MarketType).optional(),
  rawProductId: z.string().uuid().optional(),
  includeInactive: z.string().transform((val) => val === "true").optional(),
  bulkFilter: z.enum(["bulk", "non-bulk"]).optional(),
  sortBy: z.enum(["name", "sku", "stockFinal", "variance", "category"]).optional(),
  sortOrder: z.enum(["asc", "desc"]).optional()
}).refine(
  (data) => new Date(data.dateFrom) <= new Date(data.dateTo),
  {
    message: "dateFrom must be before or equal to dateTo",
    path: ["dateFrom"]
  }
);
var upsertAdminPosConfigSchema = z.object({
  provider: z.enum(["BILL"]).optional().default("BILL"),
  apiKey: z.string().min(10, "API Key: minimum 10 caracteres").max(500, "API Key: maximum 500 caracteres").optional(),
  autoSync: z.boolean().optional().default(true),
  syncInterval: z.number().int("Intervalle: doit etre un entier").min(10, "Intervalle: minimum 10 minutes").max(600, "Intervalle: maximum 600 minutes (10h)").optional().default(60),
  // Sprint webhook-catalogue-bill (US-01) : uuid Bill de la company
  // (l'enseigne, renseigne sur la marque racine) et id ENTIER de la brand
  // (le restaurant, renseigne sur chaque site — Bill n'expose pas d'uuid de
  // brand). Absent = inchange, `null` = efface (une marque sans identifiant
  // Bill n'est simplement pas concernee par le webhook, elle reste servie par
  // la synchronisation de nuit).
  billCompanyId: z.string().uuid("Uuid company Bill invalide").nullable().optional(),
  billBrandId: z.number().int("Id brand Bill : doit etre un entier").positive("Id brand Bill : doit etre positif").nullable().optional()
});
var adminSalesSyncInitSchema = z.object({
  // Accepts YYYY-MM-DD or YYYY-MM-DDTHH:mm — the backend resolves
  // the brand's cutoff hour and timezone from the date alone.
  startDate: dateOrDateTimeString
});
var purchaseSummaryReportQuerySchema = z.object({
  dateFrom: dateOrDateTimeString,
  dateTo: dateOrDateTimeString,
  supplierId: z.string().uuid().optional(),
  status: z.enum(["VALIDATED", "DRAFT", "ALL"]).optional().default("VALIDATED")
}).refine(
  (data) => new Date(data.dateFrom) <= new Date(data.dateTo),
  {
    message: "dateFrom must be before or equal to dateTo",
    path: ["dateFrom"]
  }
);
var productMixReportQuerySchema = z.object({
  dateFrom: dateOrDateTimeString,
  dateTo: dateOrDateTimeString
}).refine(
  (data) => new Date(data.dateFrom) <= new Date(data.dateTo),
  {
    message: "dateFrom must be before or equal to dateTo",
    path: ["dateFrom"]
  }
);
var supplierReturnItemSchema = z.object({
  rawProductId: z.string().uuid("Produit: ID invalide"),
  rawProductName: z.string().min(1, "Nom du produit requis"),
  packagingName: z.string().min(1, "Conditionnement requis"),
  conversionToRecipeUnit: z.number().positive("Facteur de conversion invalide"),
  quantity: z.number().int("Quantite: doit etre un entier").positive("Quantite: doit etre positive").max(1e3, "Quantite: maximum 1000"),
  unitPriceExclTax: z.number().int("Prix: doit etre en centimes (entier)").min(0, "Prix: doit etre positif ou nul").max(1e6, "Prix unitaire: max 10 000"),
  returnReason: z.nativeEnum(SupplierReturnItemReason),
  notes: z.string().max(500, "Notes: max 500 caracteres").nullable().optional()
});
var createSupplierReturnSchema = z.object({
  supplierId: z.string().uuid("Fournisseur: ID invalide"),
  returnDate: dateOrDateTimeString,
  goodsReceiptId: z.string().uuid("Reception: ID invalide").nullable().optional(),
  reason: z.string().max(500, "Motif: max 500 caracteres").nullable().optional(),
  notes: z.string().max(1e3, "Notes: max 1000 caracteres").nullable().optional(),
  items: z.array(supplierReturnItemSchema).min(1, "Au moins une ligne requise").max(100, "Maximum 100 lignes")
});
var updateSupplierReturnSchema = z.object({
  supplierId: z.string().uuid("Fournisseur: ID invalide").optional(),
  returnDate: dateOrDateTimeString.optional(),
  goodsReceiptId: z.string().uuid("Reception: ID invalide").nullable().optional(),
  reason: z.string().max(500, "Motif: max 500 caracteres").nullable().optional(),
  notes: z.string().max(1e3, "Notes: max 1000 caracteres").nullable().optional(),
  items: z.array(supplierReturnItemSchema).min(1, "Au moins une ligne requise").max(100, "Maximum 100 lignes").optional()
});
var cancelSupplierReturnSchema = z.object({
  reason: z.string().max(500, "Motif: max 500 caracteres").optional()
});
var querySupplierReturnsSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(EXPORT_LIMIT_MAX).default(20),
  status: z.enum(["DRAFT", "VALIDATED", "CANCELLED", "ALL"]).default("ALL"),
  supplierId: z.string().uuid().optional(),
  dateFrom: dateOrDateTimeString.optional(),
  dateTo: dateOrDateTimeString.optional(),
  search: z.string().optional()
});

// src/constants/api-routes.ts
var API_ROUTES = {
  AUTH: {
    BASE: "/auth",
    LOGIN: "/auth/login",
    REGISTER: "/auth/register",
    LOGOUT: "/auth/logout",
    REFRESH: "/auth/refresh",
    ME: "/auth/me",
    FORGOT_PASSWORD: "/auth/forgot-password",
    RESET_PASSWORD: "/auth/reset-password"
  },
  COMPANIES: {
    BASE: "/companies",
    BY_ID: (id) => `/companies/${id}`
  },
  BRANDS: {
    BASE: "/brands",
    BY_ID: (id) => `/brands/${id}`,
    CHILDREN: (id) => `/brands/${id}/children`
  },
  USERS: {
    BASE: "/users",
    BY_ID: (id) => `/users/${id}`
  }
};

// src/constants/backdating.ts
var BACKDATING_LIMITS_DAYS = {
  [UserRole.VIEWER]: 0,
  [UserRole.OPERATOR]: 0,
  [UserRole.MANAGER]: 2,
  [UserRole.ADMIN]: Infinity,
  [UserRole.OWNER]: Infinity
};

// src/constants/changelog.ts
var CHANGELOG = [
  {
    version: "1.9.0",
    date: "2026-10-07",
    title: "Produits Bill mieux suivis et rapport de rentabilite enrichi",
    highlights: [
      "Les produits et options supprimes dans Bill disparaissent aussi de BStock",
      "Le rapport de rentabilite affiche une ligne Food Cost Brut Bulk",
      "Dans BStaff, les heures sont affichees a l'heure du restaurant, ou que vous soyez"
    ],
    categories: {
      features: [
        "Produits de caisse : un produit fini ou une option supprime dans Bill est retire automatiquement de BStock, au lieu de rester dans vos listes",
        "Rapport de rentabilite : nouvelle ligne Food Cost Brut Bulk",
        "BStaff : les heures de pointage sont affichees et saisies a l'heure du restaurant. Un manager en deplacement ou au siege voit les vraies heures sur place, et non celles de son ordinateur"
      ],
      improvements: [
        "Utilisateurs : si l'e-mail d'activation ne peut pas partir, l'utilisateur est quand meme cree et un message vous le dit clairement",
        "Modules : une enseigne a acces a un module des qu'un de ses restaurants y est abonne"
      ],
      fixes: [
        "Rapport de stock : la colonne Ventes exclut de nouveau les repas du personnel",
        "La devise affichee est bien celle de l'enseigne selectionnee",
        "Les e-mails d'activation et de notification partent de nouveau"
      ]
    }
  },
  {
    version: "1.8.0",
    date: "2026-09-22",
    title: "Centrale d'achat : listes d'articles cloisonnees et transferts fiabilises",
    highlights: [
      "Les articles de la centrale et ceux des restaurants ne sont plus melanges",
      "Les transferts sont valorises au prix exact du catalogue interne, au centime pres",
      "Une commande contenant deux fois le meme article part bien vers la centrale"
    ],
    categories: {
      features: [
        "Articles : chaque interface n'affiche plus que les articles qui la concernent. Un article vendu uniquement par la centrale n'apparait plus dans les listes des restaurants, et un article reserve aux restaurants n'apparait plus cote centrale. Les articles utilises des deux cotes, ainsi que ceux deja mouvementes sur le site, restent visibles partout"
      ],
      improvements: [
        "Transferts : sur le bon de commande, le bon de livraison et la facture, le prix unitaire est affiche par conditionnement. Quand rien n'a encore ete expedie, c'est le conditionnement demande qui est repris, au lieu d'un prix a la piece"
      ],
      fixes: [
        "Transferts : la valorisation reprend le prix exact du catalogue interne, sans arrondi. Les ecarts de quelques centimes entre le bon de commande et la facture de transfert ont disparu",
        "Transferts : un transfert expedie apres que le restaurant a deja valide sa reception passe desormais en Livre, au lieu de rester bloque en Expedie",
        "Commandes a la centrale : une commande qui contient deux lignes du meme article cree bien sa demande de transfert. Les lignes sont regroupees et les quantites additionnees"
      ]
    }
  },
  {
    version: "1.7.0",
    date: "2026-09-15",
    title: "Type de marche modifiable",
    highlights: [
      "Le type de marche d'un produit brut se modifie directement depuis sa fiche"
    ],
    categories: {
      features: [
        "Produits bruts : dans l'onglet Infos de la fiche produit, vous pouvez changer le type de marche (Food, Food industriel, Non Food, Non Food industriel, Maintenance). Une confirmation vous rappelle que le Rapport de Marge sera recalcule avec ce nouveau classement, y compris sur les periodes passees et pour tous les sites"
      ]
    }
  },
  {
    version: "1.6.0",
    date: "2026-09-15",
    title: "Ventes plus completes et chiffre d'affaires plus juste",
    highlights: [
      "Les tickets de caisse remontes en retard par la caisse sont desormais recuperes automatiquement chaque nuit",
      "Les commandes annulees sont bien deduites du chiffre d'affaires",
      "Un encadre indique si les ventes d'un site sont completes"
    ],
    categories: {
      features: [
        `Controle Ventes POS : un encadre indique jusqu'a quelle date les ventes de chaque site sont completes, et signale les tickets de caisse encore en attente. Le bouton "Voir le detail" liste ces tickets avec leur date et leur montant`,
        "Rapport de Marge : un avertissement apparait lorsque la periode affichee contient des tickets de caisse pas encore disponibles",
        `Ventes : chaque nuit, les tickets de caisse arrives en retard sur les 45 derniers jours sont recuperes automatiquement. Ils apparaissent dans l'onglet Import Batches avec la mention "Rattrapage"`,
        `Ventes : le nombre de tickets est verifie chaque jour, chaque semaine et chaque mois. En cas d'ecart persistant, vous recevez une notification "Tickets de caisse manquants"`
      ],
      fixes: [
        "Chiffre d'affaires : les commandes annulees sans detail des produits n'etaient pas deduites. Elles le sont desormais, produit par produit, a partir de la commande d'origine",
        "Chiffre d'affaires et consommations : les supplements choisis dans un menu commande en plusieurs exemplaires n'etaient comptes qu'une seule fois. Ils sont maintenant comptes autant de fois que le menu",
        "Stock : une commande annulee ne remet plus ses produits en stock, puisqu'elle a ete preparee",
        "Ventes : une synchronisation interrompue reprend desormais la ou elle s'etait arretee"
      ]
    }
  },
  {
    version: "1.5.0",
    date: "2026-09-07",
    title: "Export Excel de toutes vos recettes",
    highlights: [
      'Un nouvel onglet "Donnees a plat" dans la page Recettes, avec export Excel en un clic'
    ],
    categories: {
      features: [
        'Recettes : le nouvel onglet "Donnees a plat" affiche toutes vos recettes et leurs ingredients dans un seul tableau, regroupe par recette. Vous depliez la recette qui vous interesse pour voir le detail de ses ingredients, avec les quantites et les unites',
        "Recettes : le bouton Exporter genere un fichier Excel contenant l'integralite de vos fiches techniques, en conservant le regroupement par recette",
        "Recettes : un champ de recherche permet de retrouver une recette, un ingredient ou une reference produit dans l'ensemble du tableau"
      ]
    }
  },
  {
    version: "1.4.3",
    date: "2026-09-07",
    title: "Separation des articles centrale d'achat et restaurants",
    highlights: [
      "Les restaurants ne voient plus les conditionnements ni les tarifs reserves a la centrale d'achat"
    ],
    categories: {
      fixes: [
        "Inventaires : les conditionnements provenant d'un fournisseur reserve a la centrale d'achat apparaissaient a tort dans les restaurants, au comptage comme sur la feuille de saisie et les exports. Chaque restaurant ne voit desormais que les conditionnements de ses propres fournisseurs",
        "Fiches produits : les tarifs d'achat negocies par la centrale d'achat n'etaient pas masques aux restaurants. Ils sont maintenant reserves a la centrale"
      ]
    }
  },
  {
    version: "1.4.2",
    date: "2026-05-22",
    title: "Reinitialisation de mot de passe corrigee",
    highlights: [
      "Les emails de reinitialisation de mot de passe sont desormais bien envoyes"
    ],
    categories: {
      fixes: [
        "Mot de passe oublie : le lien de reinitialisation est maintenant envoye par email, y compris pour les comptes administrateurs. Auparavant la demande indiquait un succes mais aucun email n'etait recu"
      ]
    }
  },
  {
    version: "1.4.1",
    date: "2026-05-06",
    title: "Correction valorisation rapport marge",
    highlights: [
      "Le rapport de marge ne compte plus 2 fois les retours fournisseurs vers la centrale d'achat"
    ],
    categories: {
      fixes: [
        "Rapport de marge : la valeur des retours fournisseurs vers votre centrale d'achat etait additionnee deux fois dans les sorties, gonflant artificiellement l'ecart vers le negatif. Le rapport affiche maintenant la bonne valorisation et concorde avec le rapport de stock"
      ]
    }
  },
  {
    version: "1.4.0",
    date: "2026-05-04",
    title: "Exports complets meme avec gros volumes",
    highlights: [
      "Les exports Excel et CSV recuperent desormais l'integralite des resultats filtres, plus seulement les lignes affichees",
      "Une alerte previent si un export depasse 10 000 lignes pour vous inviter a affiner les filtres"
    ],
    categories: {
      improvements: [
        "Mouvements de stock : un export sur une plage de dates contient toutes les lignes correspondantes (et non plus seulement la page visible)",
        "Pertes, transferts, receptions, retours fournisseur, recettes et matieres premieres : meme amelioration sur l'export"
      ]
    }
  },
  {
    version: "1.3.2",
    date: "2026-04-29",
    title: "Corrections rapports & exports",
    highlights: [
      "Le rapport de marge se charge a nouveau correctement",
      "Les colonnes calculees des exports Excel (Conso Theo., Ecart, etc.) s'affichent sur toutes les versions d'Excel"
    ],
    categories: {
      fixes: [
        "Rapport de marge : correction d'une regression qui empechait le chargement",
        "Export Excel des rapports : les valeurs calculees etaient figees a 0 sur Excel 2013, elles s'affichent maintenant correctement quelle que soit la version d'Excel"
      ]
    }
  },
  {
    version: "1.3.0",
    date: "2026-04-08",
    title: "Heure de coupure inventaires & rapports",
    highlights: [
      "Les inventaires et rapports se calent automatiquement sur l'heure de coupure du site",
      "Plus besoin de saisir l'heure : elle est appliquee automatiquement"
    ],
    categories: {
      features: [
        "Nouvelle heure de coupure configurable par site dans les parametres",
        "Les inventaires sont automatiquement dates a l'heure de coupure (ex: 7h00)",
        "Les rapports pre-remplissent les heures avec l'heure de coupure du site",
        "Les administrateurs peuvent ajuster l'heure pour les cas exceptionnels"
      ]
    }
  },
  {
    version: "1.2.1",
    date: "2026-04-02",
    title: "Ameliorations retours fournisseur et corrections",
    highlights: [
      "Selection du conditionnement sur les retours fournisseur",
      "Les retours apparaissent dans le sommaire des achats"
    ],
    categories: {
      improvements: [
        "Vous pouvez choisir le conditionnement sur chaque ligne de retour fournisseur",
        "Les retours fournisseur apparaissent en negatif dans le sommaire des achats"
      ],
      fixes: [
        "L'export Excel des pertes affiche les valorisations correctement en MAD",
        "Les noms de fichiers exportes incluent les dates de la periode"
      ]
    }
  },
  {
    version: "1.2.0",
    date: "2026-03-25",
    title: "Retours fournisseur et ameliorations",
    highlights: [
      "Nouveau module retours fournisseur",
      "Les retours impactent les rapports de stock et de rentabilite",
      "Correction de la date d'effet des inventaires"
    ],
    categories: {
      features: [
        "Vous pouvez creer des retours fournisseur (libre ou lie a une reception)",
        "Le retour genere un bon de livraison PDF avec les valorisations",
        "Les retours apparaissent dans le journal des mouvements avec lien direct",
        "Les retours sont pris en compte dans les rapports de stock et de rentabilite"
      ],
      improvements: [
        "La page des pertes utilise un selecteur de dates avec heure",
        "La ligne Consommation Maintenance est toujours visible sur le rapport de rentabilite"
      ],
      fixes: [
        "La date d'effet des inventaires respecte le fuseau horaire du restaurant"
      ]
    }
  },
  {
    version: "1.1.0",
    date: "2026-03-24",
    title: "Exports documents et corrections rapports",
    highlights: [
      "Telechargez tous les bons de reception d'une periode en un seul PDF",
      "L'inventaire valide genere aussi un fichier Excel",
      "Les ecarts des rapports sont maintenant corrects et coherents"
    ],
    categories: {
      features: [
        "Vous pouvez telecharger tous les bons de reception d'une periode en un seul PDF",
        "L'inventaire valide genere maintenant un fichier Excel en plus du PDF",
        "La reference produit (SKU) est affichee sur le PDF d'inventaire"
      ],
      improvements: [
        "Les onglets transferts sont renommes pour plus de clarte",
        "Les noms de fichiers exportes incluent les dates du rapport",
        "Le sommaire des achats affiche la date de creation et la date d'effet"
      ],
      fixes: [
        "Les ecarts du rapport de rentabilite (Portions Controle) sont maintenant corrects",
        "Le rapport de stock ne compte plus les receptions annulees en double",
        "Les remises sont correctement interpretees lors de l'import des ventes",
        "Si deux inventaires tombent a la meme date, seul le plus recent est pris en compte"
      ]
    }
  },
  {
    version: "1.0.0",
    date: "2026-03-19",
    title: "Version initiale",
    highlights: [
      "Premiere version officielle de BStock",
      "Suivi des mises a jour disponible depuis le menu lateral"
    ],
    categories: {
      features: [
        "Vous pouvez desormais consulter les nouveautes de chaque mise a jour directement dans l'application"
      ]
    }
  }
];

// src/constants/config.ts
var CONFIG = {
  /** Pagination par défaut */
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  /** Timeouts */
  API_TIMEOUT: 3e4,
  // 30s
  /** JWT */
  ACCESS_TOKEN_EXPIRES_IN: "15m",
  REFRESH_TOKEN_EXPIRES_IN: "7d",
  /** Formats de date */
  DATE_FORMAT: "yyyy-MM-dd",
  DATETIME_FORMAT: "yyyy-MM-dd HH:mm:ss",
  TIME_FORMAT: "HH:mm"
};

// src/constants/notification-defaults.ts
var NOTIFICATION_DEFAULTS = {
  // Transferts - demandeur
  TRANSFER_SENT: {
    inApp: true,
    email: false,
    label: "Demande envoyee",
    category: "Transferts"
  },
  TRANSFER_SHIPPED_BY_DEST: {
    inApp: true,
    email: true,
    label: "Transfert expedie (par le fournisseur)",
    category: "Transferts"
  },
  TRANSFER_RECEIVED_BY_YOU: {
    inApp: true,
    email: false,
    label: "Transfert receptionne",
    category: "Transferts"
  },
  TRANSFER_CANCELLED_BY_YOU: {
    inApp: true,
    email: false,
    label: "Demande annulee",
    category: "Transferts"
  },
  // Transferts - destinataire
  TRANSFER_INCOMING: {
    inApp: true,
    email: true,
    label: "Nouvelle demande recue",
    category: "Transferts"
  },
  TRANSFER_SHIPPED_BY_YOU: {
    inApp: true,
    email: false,
    label: "Transfert expedie (par moi)",
    category: "Transferts"
  },
  TRANSFER_RECEIVED_BY_SOURCE: {
    inApp: true,
    email: false,
    label: "Receptionne par le demandeur",
    category: "Transferts"
  },
  TRANSFER_CANCELLED_BY_SOURCE: {
    inApp: true,
    email: true,
    label: "Annule par le demandeur",
    category: "Transferts"
  },
  // Inventaires
  INVENTORY_CREATED: {
    inApp: true,
    email: false,
    label: "Inventaire cree",
    category: "Inventaires"
  },
  INVENTORY_VALIDATED: {
    inApp: true,
    email: true,
    label: "Inventaire valide",
    category: "Inventaires"
  },
  // Receptions
  GOODS_RECEIPT_CREATED: {
    inApp: true,
    email: false,
    label: "Reception creee",
    category: "Receptions"
  },
  GOODS_RECEIPT_VALIDATED: {
    inApp: true,
    email: true,
    label: "Reception validee",
    category: "Receptions"
  },
  // Commandes
  PURCHASE_ORDER_VALIDATED: {
    inApp: true,
    email: true,
    label: "Commande validee",
    category: "Commandes"
  },
  PURCHASE_ORDER_RECEIVED_INTERNAL: {
    inApp: true,
    email: true,
    label: "Commande recue (fournisseur interne)",
    category: "Commandes"
  },
  // Pertes
  LOSS_CREATED: {
    inApp: true,
    email: true,
    label: "Perte enregistree",
    category: "Pertes"
  },
  // Ventes
  SALES_COMPLETENESS_ALERT: {
    inApp: true,
    email: true,
    label: "Tickets de caisse manquants",
    category: "Ventes"
  }
};
var ALL_NOTIFICATION_TYPES = Object.keys(NOTIFICATION_DEFAULTS);
var EMAIL_SUBJECTS = {
  TRANSFER_SENT: "Demande de transfert envoyee",
  TRANSFER_SHIPPED_BY_DEST: "Transfert expedie",
  TRANSFER_RECEIVED_BY_YOU: "Transfert receptionne",
  TRANSFER_CANCELLED_BY_YOU: "Demande de transfert annulee",
  TRANSFER_INCOMING: "Nouvelle demande de transfert",
  TRANSFER_SHIPPED_BY_YOU: "Transfert expedie",
  TRANSFER_RECEIVED_BY_SOURCE: "Transfert receptionne",
  TRANSFER_CANCELLED_BY_SOURCE: "Demande de transfert annulee",
  INVENTORY_CREATED: "Nouvel inventaire",
  INVENTORY_VALIDATED: "Inventaire valide",
  GOODS_RECEIPT_CREATED: "Nouvelle reception",
  GOODS_RECEIPT_VALIDATED: "Reception validee",
  PURCHASE_ORDER_VALIDATED: "Commande validee",
  PURCHASE_ORDER_RECEIVED_INTERNAL: "Nouvelle commande interne",
  LOSS_CREATED: "Perte enregistree",
  SALES_COMPLETENESS_ALERT: "Tickets de caisse manquants"
};

// src/constants/version.ts
var APP_VERSION = "1.9.0";
function formatDate(date, pattern = "dd/MM/yyyy") {
  const dateObj = typeof date === "string" ? parseISO(date) : date;
  if (!isValid(dateObj)) return "";
  return format(dateObj, pattern, { locale: fr });
}
function formatDateTime(date) {
  return formatDate(date, "dd/MM/yyyy \xE0 HH:mm");
}
function isValidDate(date) {
  return date instanceof Date && isValid(date);
}

// src/utils/currency.utils.ts
function formatCurrency(amount, options) {
  const { showSymbol = true, decimals = 2 } = options || {};
  const formatted = new Intl.NumberFormat("fr-FR", {
    style: "decimal",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(amount);
  return showSymbol ? `${formatted} \u20AC` : formatted;
}
function roundCurrency(amount) {
  return Math.round(amount * 100) / 100;
}

// src/utils/string.utils.ts
function slugify(text) {
  return text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}
function capitalize(text) {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}
function truncate(text, maxLength) {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + "...";
}
function validateSchema(schema, data) {
  try {
    const validatedData = schema.parse(data);
    return { success: true, data: validatedData };
  } catch (error) {
    if (error instanceof ZodError) {
      const errors = {};
      error.issues.forEach((err) => {
        const path = err.path.join(".");
        errors[path] = err.message;
      });
      return { success: false, errors };
    }
    throw error;
  }
}
async function validateSchemaAsync(schema, data) {
  try {
    const validatedData = await schema.parseAsync(data);
    return { success: true, data: validatedData };
  } catch (error) {
    if (error instanceof ZodError) {
      const errors = {};
      error.issues.forEach((err) => {
        const path = err.path.join(".");
        errors[path] = err.message;
      });
      return { success: false, errors };
    }
    throw error;
  }
}

// src/utils/unit-display.utils.ts
var DISPLAY_UNITS = {
  ["MASS" /* MASS */]: { symbol: "kg", factorFromBase: 1e3 },
  // 1 kg = 1000 g
  ["VOLUME" /* VOLUME */]: { symbol: "L", factorFromBase: 1e3 },
  // 1 L = 1000 ml
  ["PIECE" /* PIECE */]: { symbol: "pce", factorFromBase: 1 }
  // pas de conversion
};
function getDisplayUnitPrice(unitPriceInBase, unitType) {
  const config = DISPLAY_UNITS[unitType];
  return {
    displayUnitPrice: unitPriceInBase * config.factorFromBase,
    displayUnitSymbol: config.symbol
  };
}
function getDisplayUnitConfig(unitType) {
  return DISPLAY_UNITS[unitType];
}

// src/utils/conversion-helper.ts
var ConversionMode = /* @__PURE__ */ ((ConversionMode2) => {
  ConversionMode2["AUTO"] = "AUTO";
  ConversionMode2["MANUAL"] = "MANUAL";
  ConversionMode2["NONE"] = "NONE";
  return ConversionMode2;
})(ConversionMode || {});
var unitTypeLabels = {
  ["MASS" /* MASS */]: "masse",
  ["VOLUME" /* VOLUME */]: "volume",
  ["PIECE" /* PIECE */]: "piece"
};
function getConversionInfo(packagingQuantity, packagingUnit, recipeUnit) {
  if (packagingUnit.type === "PIECE" /* PIECE */ && recipeUnit.type === "PIECE" /* PIECE */) {
    return {
      mode: "MANUAL" /* MANUAL */,
      value: null,
      message: "Saisir le nombre de pieces par conditionnement",
      isEditable: true
    };
  }
  if (packagingUnit.code === recipeUnit.code) {
    return {
      mode: "NONE" /* NONE */,
      value: packagingQuantity,
      message: "Aucune conversion necessaire (meme unite)",
      isEditable: false
    };
  }
  if (packagingUnit.type === recipeUnit.type) {
    const rawValue = packagingQuantity * packagingUnit.conversionFactor / recipeUnit.conversionFactor;
    const value = Math.round(rawValue * 1e4) / 1e4;
    const displayValue = value.toLocaleString("fr-FR", {
      maximumFractionDigits: 4,
      useGrouping: true
    });
    return {
      mode: "AUTO" /* AUTO */,
      value,
      message: `Conversion automatique : 1 conditionnement = ${displayValue} ${recipeUnit.code}`,
      isEditable: false
    };
  }
  const fromType = unitTypeLabels[packagingUnit.type];
  const toType = unitTypeLabels[recipeUnit.type];
  return {
    mode: "MANUAL" /* MANUAL */,
    value: null,
    message: `Conversion manuelle necessaire (${fromType} \u2192 ${toType})`,
    isEditable: true
  };
}

// src/permissions/permissions.ts
function canSeeFinancials(role) {
  return hasPermission(role, "stock.read_financials");
}

export { ALL_NOTIFICATION_TYPES, API_ROUTES, APP_VERSION, ActivationStatus, AppSource, BACKDATING_LIMITS_DAYS, BREAKDOWN_REASON_LABELS, BrandType, CHANGELOG, CONFIG, CURRENCY_INFO, ConversionMode, Currency, DEFAULT_CURRENCY, DOCUMENT_TYPE_LABELS, DocumentEntityType, DocumentType, EMAIL_SUBJECTS, EXPORT_LIMIT_MAX, GOODS_RECEIPT_STATUS_COLORS, GOODS_RECEIPT_STATUS_LABELS, GoodsReceiptStatus, INVENTORY_STATUS_COLORS, INVENTORY_STATUS_LABELS, INVENTORY_TYPE_COLORS, INVENTORY_TYPE_LABELS, InventoryStatus, InventoryType, LOSS_REASON_COLORS, LOSS_REASON_LABELS, LOSS_REASON_OPTIONS, LOSS_TYPE_COLORS, LOSS_TYPE_LABELS, LOSS_TYPE_OPTIONS, LossReason, MOVEMENT_REASON_COLORS, MOVEMENT_REASON_LABELS, MOVEMENT_TYPE_LABELS, MarginReportStepLabels, MarketType, MarketTypeIcons, MarketTypeLabels, MovementReason, MovementType, NOTIFICATION_DEFAULTS, OrderStatus, PAGE_SIZE_DEFAULT, PAGE_SIZE_MAX, PERMISSION_MATRIX, PMPSourceType, PMP_SOURCE_TYPE_LABELS, PURCHASE_ORDER_STATUS_COLORS, PURCHASE_ORDER_STATUS_LABELS, PosProductSortField, PosProductType, PosProvider, PosSyncStatus, PurchaseOrderStatus, RoleHierarchy, SOURCE_TYPE_LABELS, STOCK_STATUS_COLORS, STOCK_STATUS_LABELS, SUPPLIER_RETURN_ITEM_REASON_LABELS, SUPPLIER_RETURN_STATUS_COLORS, SUPPLIER_RETURN_STATUS_LABELS, SUPPORTED_CURRENCIES, SalesImportStepLabels, SortOrder, SourceType, Status, SupplierAccessScope, SupplierReturnItemReason, SupplierReturnStatus, SyncStatus, TRANSFER_STATUS_COLORS, TRANSFER_STATUS_LABELS, TRANSFER_STATUS_LABELS_REQUESTER, TRANSFER_STATUS_LABELS_SUPPLIER, TRANSFER_STATUS_OPTIONS, TokenType, TransferStatus, UnitType, UnitTypeLabels, UserRole, VARIANCE_REASON_LABELS, VarianceReason, addIngredientSchema, addOrderItemSchema, adminSalesSyncInitSchema, auditActionEnum, canCancelTransfer, canEditTransfer, canReceiveTransfer, canSeeFinancials, canShipTransfer, canSubmitTransfer, cancelInventorySchema, cancelSupplierReturnSchema, cancelTransferSchema, capitalize, convertQuerySchema, createAdminBrandSchema, createAdminCompanySchema, createBrandSchema, createCatalogItemSchema, createCategorySchema, createCompanySchema, createFinishedProductLossSchema, createGoodsReceiptSchema, createInventoryListSchema, createInventorySchema, createLossSchema, createPackagingSchema, createPurchaseOrderSchema, createRawProductSchema, createRecipeSchema, createSupplierReturnSchema, createSupplierSchema, createTransferLineSchema, createTransferSchema, createUserSchema, createUserWithPasswordSchema, dateOrDateTimeString, duplicateRecipeSchema, forgotPasswordSchema, formatCurrency, formatDate, formatDateTime, getConversionInfo, getDisplayUnitConfig, getDisplayUnitPrice, getQuantitySign, getSiteRole, getTransferStatusLabel, getUsersQuerySchema, goodsReceiptItemSchema, hasMinimumRole, hasPermission, importSourceLabels, initReceiptFromOrderSchema, inventoryLineInputSchema, isDestinationSite, isInboundMovement, isOutboundMovement, isSourceSite, isValidDate, isoDateTimeString, listCategoriesQuerySchema, listUnitsQuerySchema, loginSchema, moveCategorySchema, orderItemSchema, passwordRules, passwordSchema, previewFinishedProductLossSchema, productMixReportQuerySchema, publishRecipeSchema, purchaseSummaryReportQuerySchema, queryAuditLogsSchema, queryAuditStatsSchema, queryCatalogItemsSchema, queryCurrentStockSchema, queryEntityHistorySchema, queryGoodsReceiptsSchema, queryInventoriesSchema, queryInventoryListsSchema, queryLossKPIsSchema, queryLossesSchema, queryPMPHistorySchema, queryPosProductsSchema, queryPurchaseOrdersSchema, queryRawProductsSchema, queryRecipeAtDateSchema, queryRecipesSchema, queryStockBreakdownSchema, querySupplierReturnsSchema, querySuppliersSchema, queryTransfersSchema, receiveTransferLineSchema, receiveTransferSchema, recipeIngredientInputSchema, reorderIngredientsSchema, roundCurrency, saleTypeLabels, saveProductLinesSchema, setPasswordSchema, shipTransferLineSchema, shipTransferSchema, slugify, stockReportQuerySchema, supplierReturnItemSchema, syncPosProductsSchema, truncate, unitTypeSchema, updateAdminBrandSchema, updateAdminCompanySchema, updateBrandSchema, updateBrandSettingsSchema, updateCatalogItemPriceSchema, updateCatalogItemSchema, updateCategorySchema, updateCompanyBrandingSchema, updateCompanySchema, updateGoodsReceiptItemSchema, updateGoodsReceiptSchema, updateIngredientSchema, updateInventoryListItemsSchema, updateInventoryListSchema, updateInventorySchema, updateOrderItemSchema, updatePackagingSchema, updatePurchaseOrderSchema, updateRawProductSchema, updateRecipeSchema, updateSupplierReturnSchema, updateSupplierSchema, updateTransferSchema, updateUserRoleSchema, updateUserSchema, upsertAdminPosConfigSchema, validateSchema, validateSchemaAsync, validateTokenSchema };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map